// src/components/SuperImposableLoad.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import { FaCalendarAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import "../styles/SuperImposableLoad.css";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { useTranslation } from "react-i18next";

const SuperImposableLoad = () => {
  const { t } = useTranslation("super_imposable_load");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  const [createdLoadId, setCreatedLoadId] = useState(null);

  const [lengthInput, setLengthInput] = useState("");
  const [heightInput, setHeightInput] = useState("");
  const [widthInput, setWidthInput] = useState("");

  // 🆕 Weight
  const [weightInput, setWeightInput] = useState("");
  const [weightError, setWeightError] = useState("");

  const [merchandise, setMerchandise] = useState("");
  const [merchandiseError, setMerchandiseError] = useState("");

  const [error, setError] = useState("");
  const [lengthError, setLengthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [widthError, setWidthError] = useState("");

  const [priceInput, setPriceInput] = useState("");
  const [priceError, setPriceError] = useState("");

  // 🆕 Price for subcontracted (optional, only when external carrier selected)
  const [price4subcontratedInput, setPrice4subcontratedInput] = useState("");
  const [price4subcontratedError, setPrice4subcontratedError] = useState("");

  // 📝 Notes for driver (optional)
  const [notes4driver, setNotes4driver] = useState("");
  const [notes4driverError, setNotes4driverError] = useState("");

  // 🆕 Notes for subcontracted (optional, only when external carrier selected)
  const [notes4subcontracted, setNotes4subcontracted] = useState("");
  const [notes4subcontractedError, setNotes4subcontractedError] = useState("");

  // 🆕 Trailer Type
  const [trailerType, setTrailerType] = useState(null); // ex: "TAUTLINER" | "FRIGO" | "OTHER" | ...
  const [trailerTypeError, setTrailerTypeError] = useState(false);
  const [trailerTypeOther, setTrailerTypeOther] = useState("");
  const [trailerTypeOtherError, setTrailerTypeOtherError] = useState(false);

  const [loadBearers, setLoadBearers] = useState([]);
  const [selectedLoadBearer, setSelectedLoadBearer] = useState("");
  const [remainingSpace, setRemainingSpace] = useState(null);

  const maxLength = 13.6; // Max allowed length per transport session
  const MAX_POINTS = 7;

  const [showWarning, setShowWarning] = useState(false);
  const [highlightDropdown, setHighlightDropdown] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleCloseSuccessPopup = () => {
    setSuccessPopupOpen(false);
    navigate("/loads");
  };

  const [pickUpPlaces, setPickUpPlaces] = useState([
    {
      location: "",
      date: "",
      dateError: false,
      hour: "",
      hourError: false,

      // 🆕 point-level dimensions
      length: "",
      width: "",
      height: "",
      lengthError: "",
      widthError: "",
      heightError: "",

      placeId: null,
      address: "",
      postalCode: "",
      city: "",
      country: "",
      gMapsLink: "",
      coordinates: "",
      contactPhone: "",

      // 🆕 Loading Access
      loadingAccess: "SIDE",
      loadingAccessError: false,

      // 🆕 Handling Equipment
      handlingEquipment: "FORKLIFT",
      handlingEquipmentError: false,
      handlingEquipmentOther: "",
      handlingEquipmentOtherError: false,

      // 🆕 Reference (optional)
      reference: "",

      showMoreFields: false,
    },
  ]);

  const [deliveryPlaces, setDeliveryPlaces] = useState([
    {
      location: "",
      date: "",
      dateError: false,
      hour: "",
      hourError: false,

      // 🆕 point-level dimensions
      length: "",
      width: "",
      height: "",
      lengthError: "",
      widthError: "",
      heightError: "",

      placeId: null,
      address: "",
      postalCode: "",
      city: "",
      country: "",
      gMapsLink: "",
      coordinates: "",
      contactPhone: "",

      // 🆕 Loading Access
      loadingAccess: "SIDE",
      loadingAccessError: false,

      // 🆕 Handling Equipment
      handlingEquipment: "FORKLIFT",
      handlingEquipmentError: false,
      handlingEquipmentOther: "",
      handlingEquipmentOtherError: false,

      // 🆕 Reference (optional)
      reference: "",

      showMoreFields: false,
    },
  ]);

  const [pickUpPlaceError, setPickUpPlaceError] = useState("");
  const [deliveryPlaceError, setDeliveryPlaceError] = useState("");
  const [existingDateHourConflicts, setExistingDateHourConflicts] = useState([]);

  const dateRefs = useRef([]);
  const hourRefs = useRef([]);
  const deliveryDateRefs = useRef([]);
  const deliveryHourRefs = useRef([]);

  const [globalHourFormatError, setGlobalHourFormatError] = useState(false);

  // ---------------------------
  // 🆕 Loading Access
  // ---------------------------
  const loadingAccessOptions = [
    { value: "FRONT", label: t("loadingAccess.options.front", { defaultValue: "Front" }) },
    { value: "SIDE", label: t("loadingAccess.options.side", { defaultValue: "Side" }) },
    { value: "REAR", label: t("loadingAccess.options.rear", { defaultValue: "Rear" }) },
    { value: "TOP", label: t("loadingAccess.options.top", { defaultValue: "Top" }) },
    { value: "UNKNOWN", label: t("loadingAccess.options.unknown", { defaultValue: "Unknown" }) },
  ];

  // ---------------------------
  // 🆕 Handling Equipment
  // ---------------------------
  const handlingEquipmentOptions = [
    { value: "FORKLIFT",             label: t("fields.handlingEquipment.forklift", { defaultValue: "Forklift" }) },
    { value: "PALLET_JACK",          label: t("fields.handlingEquipment.palletJack", { defaultValue: "Pallet jack" }) },
    { value: "ELECTRIC_PALLET_JACK", label: t("fields.handlingEquipment.electricPalletJack", { defaultValue: "Electric pallet jack" }) },
    { value: "CRANE",                label: t("fields.handlingEquipment.crane", { defaultValue: "Crane" }) },
    { value: "LOADING_RAMP",         label: t("fields.handlingEquipment.loadingRamp", { defaultValue: "Loading ramp" }) },
    { value: "TAIL_LIFT",            label: t("fields.handlingEquipment.tailLift", { defaultValue: "Tail lift" }) },
    { value: "HAND_LOAD",            label: t("fields.handlingEquipment.handLoad", { defaultValue: "Hand load" }) },
    { value: "CONVEYOR",             label: t("fields.handlingEquipment.conveyor", { defaultValue: "Conveyor belt" }) },
    { value: "OTHER",                label: t("fields.handlingEquipment.other", { defaultValue: "Other" }) },
  ];

  // ---------------------------
  // 🆕 Trailer Type (⚠️ manter values iguais ao enum do backend)
  // ---------------------------
  const trailerTypeOptions = [
    { value: "ANY", label: t("trailerType.options.any", { defaultValue: "Any" }) },

    { value: "TAUTLINER", label: t("trailerType.options.tautliner", { defaultValue: "Tautliner / Curtain" }) },
    { value: "REEFER", label: t("trailerType.options.reefer", { defaultValue: "Reefer / Frigo" }) },
    { value: "BOX", label: t("trailerType.options.box", { defaultValue: "Box" }) },
    { value: "FLATBED", label: t("trailerType.options.flatbed", { defaultValue: "Flatbed" }) },
    { value: "LOWBED", label: t("trailerType.options.lowbed", { defaultValue: "Lowbed" }) },
    { value: "TANKER", label: t("trailerType.options.tanker", { defaultValue: "Tanker" }) },
    { value: "TIPPER", label: t("trailerType.options.tipper", { defaultValue: "Tipper" }) },
    { value: "CONTAINER_CHASSIS", label: t("trailerType.options.containerChassis", { defaultValue: "Container chassis" }) },
    { value: "MEGA", label: t("trailerType.options.mega", { defaultValue: "Mega" }) },

    { value: "OTHER", label: t("trailerType.options.other", { defaultValue: "Other" }) },
  ];

  // Extract parameters from URL
  const params = new URLSearchParams(location.search);
  const existingTransportSessionId = params.get("transportsessionId");

  const [industryClientId, setIndustryClientId] = useState("");
  const [carrierClientId, setCarrierClientId] = useState("");
  const [industryClients, setIndustryClients] = useState([]);
  const [carrierClients, setCarrierClients] = useState([]);
  const [carrierId, setCarrierId] = useState(""); // THE CARRIER THAT WILL TRANSPORT THE LOAD

  const [selectedClientContactEmails, setSelectedClientContactEmails] = useState([]);
  const [selectedSubcontractedEmails, setSelectedSubcontractedEmails] = useState([]);

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  };

  const extractClientContactEmails = (clientObj) => {
    if (!clientObj || typeof clientObj !== "object") return [];
  
    const found = new Set();
  
    const addEmail = (value) => {
      const email = String(value || "").trim();
      if (!email) return;
      if (!isValidEmail(email)) return;
      found.add(email.toLowerCase());
    };
  
    addEmail(clientObj.contactEmail);
    addEmail(clientObj.email);
    addEmail(clientObj.mainContactEmail);
    addEmail(clientObj.secondaryContactEmail);
  
    const possibleCollections = [
      clientObj.contacts,
      clientObj.contactPersons,
      clientObj.contactPeople,
    ];
    
    possibleCollections.forEach((collection) => {
      let parsedCollection = collection;
    
      if (typeof parsedCollection === "string") {
        try {
          parsedCollection = JSON.parse(parsedCollection);
        } catch {
          parsedCollection = [];
        }
      }
    
      if (Array.isArray(parsedCollection)) {
        parsedCollection.forEach((contact) => {
          addEmail(contact?.email);
          addEmail(contact?.contactEmail);
        });
      }
    });
  
    return Array.from(found).map((email) => ({
      value: email,
      label: email,
    }));
  };

  // 🆕 for internal vs external carrier detection (same logic as CreateLoad)
  const [companyName, setCompanyName] = useState("");
  const [availableCarriers, setAvailableCarriers] = useState([]);

  // 🆕 selected carrier option (from dropdown)
  const selectedTransportOptMemo = useMemo(() => {
    return (availableCarriers || []).find((c) => String(c.value) === String(carrierId)) || null;
  }, [availableCarriers, carrierId]);

  // 🆕 your internal carrier is the "__internal__" option you build
  const isInternalCarrierMemo = useMemo(() => {
    return selectedTransportOptMemo?.value === "__internal__";
  }, [selectedTransportOptMemo]);

  // 🆕 show notes4subcontracted ONLY if carrier is external (not internal)
  const showNotes4subcontracted = useMemo(() => {
    if (!carrierId) return false;
    if (isInternalCarrierMemo) return false;

    const own = String(companyName || "").trim().toLowerCase();
    const sel = String(selectedTransportOptMemo?.label || "").trim().toLowerCase();

    if (!own || !sel) return true; // if can't compare, still treat as external
    return sel !== own;
  }, [carrierId, isInternalCarrierMemo, companyName, selectedTransportOptMemo]);

  // 🧼 if user switches back to internal carrier, clear subcontracted notes
  useEffect(() => {
    if (!showNotes4subcontracted) {
      setNotes4subcontracted("");
      setNotes4subcontractedError("");
  
      // 🆕 also clear subcontracted price
      setPrice4subcontratedInput("");
      setPrice4subcontratedError("");
  
      setSelectedSubcontractedEmails([]);
    }
  }, [showNotes4subcontracted]);

  const [clientType, setClientType] = useState(""); // "industry" | "carrier"

  const selectedClientContactEmailOptions = useMemo(() => {
    if (!clientType) return [];
  
    const selectedId =
      clientType === "industry" ? industryClientId : carrierClientId;
  
    if (!selectedId) return [];
  
    const sourceList = clientType === "industry" ? industryClients : carrierClients;
  
    const fullClient = (sourceList || []).find(
      (c) => String(c.id) === String(selectedId)
    );
  
    return extractClientContactEmails(fullClient || null);
  }, [clientType, industryClientId, carrierClientId, industryClients, carrierClients]);
  
  const selectedSubcontractedEmailOptions = useMemo(() => {
    if (!showNotes4subcontracted || !carrierId || carrierId === "__internal__") return [];
  
    const fullCarrier = (carrierClients || []).find(
      (c) => String(c.id) === String(carrierId)
    );
  
    return extractClientContactEmails(fullCarrier || null);
  }, [showNotes4subcontracted, carrierId, carrierClients]);

  const multiSelectHorizontalStyles = useMemo(() => {
    const base = customSelectStyles || {};

    return {
      ...base,

      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        minHeight: 44,
        height: "auto",
        overflow: "hidden",
        alignItems: "center",
      }),

      valueContainer: (provided, state) => ({
        ...(base.valueContainer ? base.valueContainer(provided, state) : provided),

        // ✅ tags sempre numa linha
        flexWrap: "nowrap",

        // ✅ scroll horizontal quando há várias opções
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        // ✅ altura/alinhamento consistente
        alignItems: "center",
        paddingTop: 6,
        paddingBottom: 6,

        // ✅ espaço para clear + seta
        paddingRight: 72,

        gap: 6,
      }),

      multiValue: (provided, state) => ({
        ...(base.multiValue ? base.multiValue(provided, state) : provided),
        flex: "0 0 auto",
        maxWidth: "none",
        margin: 0,
      }),

      multiValueLabel: (provided, state) => ({
        ...(base.multiValueLabel ? base.multiValueLabel(provided, state) : provided),
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),

      indicatorsContainer: (provided, state) => ({
        ...(base.indicatorsContainer ? base.indicatorsContainer(provided, state) : provided),
        alignItems: "center",
        paddingTop: 0,
      }),

      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }, []);

  const [forceCloseClientTypeSelect, setForceCloseClientTypeSelect] = useState(false);
  const [forceCloseClientSelect, setForceCloseClientSelect] = useState(false);

  const [placeOptions, setPlaceOptions] = useState([]);

  const [dateErrorModal, setDateErrorModal] = useState({
    visible: false,
    type: "",
    index: null,
  });

  const [triedSubmit, setTriedSubmit] = useState(false);

  // ---------------------------
  // Fetch places
  // ---------------------------
  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const response = await axios.get("/places");
        const formatted = response.data.map((p) => ({
          value: p.id,
          label: p.placeName,
          fullData: p,
        }));
        setPlaceOptions(formatted);
      } catch (error) {
        console.error("❌ Error fetching places:", error);
      }
    };

    fetchPlaces();
  }, []);


    // 🆕 fetch your company name (same as CreateLoad)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get("/auth/whoami");
        if (!alive) return;
        setCompanyName(String(res.data?.company_name || "").trim());
      } catch (e) {
        console.warn("whoami fetch failed:", e?.response?.status);
        setCompanyName("");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);


  // ---------------------------
  // Fetch Load Bearers with remaining space
  // ---------------------------
  const fetchLoadBearers = useCallback(async (transportsessionId) => {
    try {
      const response = await axios.get(`/loads/loadbearers-with-space/${transportsessionId}`);
      if (response.status === 200) {
        const filtered = response.data
          .filter((lb) => lb.remainingSpace > 0)
          .map((lb) => ({
            ...lb,
            height: lb.height !== undefined ? parseFloat(lb.height) : 0,
            width: lb.width !== undefined ? parseFloat(lb.width) : 0,
          }));
        setLoadBearers(filtered);
        if (filtered.length > 0) {
          setRemainingSpace(filtered[0].remainingSpace);
        }
      }
    } catch (error) {
      console.error("❌ Error fetching optimized Load Bearers:", error);
    }
  }, []);

  useEffect(() => {
    dateRefs.current = pickUpPlaces.map((_, i) => dateRefs.current[i] || null);
    hourRefs.current = pickUpPlaces.map((_, i) => hourRefs.current[i] || null);
    deliveryDateRefs.current = deliveryPlaces.map((_, i) => deliveryDateRefs.current[i] || null);
    deliveryHourRefs.current = deliveryPlaces.map((_, i) => deliveryHourRefs.current[i] || null);
  }, [pickUpPlaces, deliveryPlaces]);

  useEffect(() => {
    if (existingTransportSessionId) {
      fetchLoadBearers(existingTransportSessionId);
    }
  }, [existingTransportSessionId, fetchLoadBearers]);

  // Existing pickup/delivery points to detect conflicts
  useEffect(() => {
    const fetchExistingPoints = async () => {
      if (!existingTransportSessionId) return;
      try {
        const response = await axios.get(`/pickupdeliveries/${existingTransportSessionId}`);
        setExistingDateHourConflicts(response.data);
      } catch (err) {
        console.error("❌ Error fetching existing pickup/delivery points:", err);
      }
    };
    fetchExistingPoints();
  }, [existingTransportSessionId]);

  // Conflicts (same date+hour as existing)
  useEffect(() => {
    const allNewPoints = [...pickUpPlaces, ...deliveryPlaces];
    const conflictMessages = [];

    allNewPoints.forEach((newPoint) => {
      if (!newPoint.date) return;

      const match = existingDateHourConflicts.find(
        (existing) => existing.date === newPoint.date && existing.hour === newPoint.hour
      );

      if (match) {
        conflictMessages.push(
          t("conflicts.existingPoint", {
            type: String(match.type || "").toUpperCase(),
            location: match.location,
            date: match.date,
            hour: match.hour,
          })
        );
      }
    });

    if (conflictMessages.length > 0) setError(conflictMessages.join("\n"));
    else setError("");
  }, [pickUpPlaces, deliveryPlaces, existingDateHourConflicts, t]);

  // Delivery cannot be before pickup
  useEffect(() => {
    let foundError = false;
    const updatedDeliveries = [...deliveryPlaces];

    for (let i = 0; i < deliveryPlaces.length; i++) {
      const delivery = deliveryPlaces[i];
      updatedDeliveries[i].dateError = false;
      updatedDeliveries[i].hourError = false;

      for (const pickup of pickUpPlaces) {
        if (pickup.date && pickup.hour && delivery.date && delivery.hour) {
          const pickupDateTime = new Date(`${pickup.date}T${pickup.hour}`);
          const deliveryDateTime = new Date(`${delivery.date}T${delivery.hour}`);

          if (deliveryDateTime < pickupDateTime) {
            updatedDeliveries[i].dateError = delivery.date !== pickup.date;
            updatedDeliveries[i].hourError = delivery.date === pickup.date;
            foundError = true;
            break;
          }
        }
      }

      if (foundError) break;
    }

    const hasChanged = updatedDeliveries.some(
      (updated, i) =>
        updated.dateError !== deliveryPlaces[i].dateError || updated.hourError !== deliveryPlaces[i].hourError
    );

    if (hasChanged) setDeliveryPlaces(updatedDeliveries);

    if (!foundError && error === t("errors.deliveryBeforePickup")) {
      if (error !== "") setError("");
    } else if (foundError) {
      if (error !== t("errors.deliveryBeforePickup")) setError(t("errors.deliveryBeforePickup"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickUpPlaces, deliveryPlaces, t]);

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const [industriesRes, carriersRes] = await Promise.all([axios.get("/industries"), axios.get("/carriers")]);

        const sortedIndustries = industriesRes.data.sort((a, b) =>
          a.companyName.localeCompare(b.companyName, undefined, { sensitivity: "base" })
        );

        const sortedCarriers = carriersRes.data.sort((a, b) =>
          a.companyName.localeCompare(b.companyName, undefined, { sensitivity: "base" })
        );

        setIndustryClients(sortedIndustries);
        setCarrierClients(sortedCarriers);
      } catch (error) {
        console.error("❌ Error fetching clients:", error);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    const selectedId = clientType === "industry" ? industryClientId : carrierClientId;
  
    if (!clientType || !selectedId) {
      setSelectedClientContactEmails([]);
      return;
    }
  
    if (!selectedClientContactEmailOptions.length) {
      setSelectedClientContactEmails([]);
      return;
    }
  
    setSelectedClientContactEmails((prev) => {
      if (Array.isArray(prev) && prev.length > 0) return prev;
      return selectedClientContactEmailOptions.slice(0, 3);
    });
  }, [clientType, industryClientId, carrierClientId, selectedClientContactEmailOptions]);
  
  useEffect(() => {
    if (!showNotes4subcontracted) {
      setSelectedSubcontractedEmails([]);
      return;
    }
  
    if (!selectedSubcontractedEmailOptions.length) {
      setSelectedSubcontractedEmails([]);
      return;
    }
  
    setSelectedSubcontractedEmails((prev) => {
      if (Array.isArray(prev) && prev.length > 0) return prev;
      return selectedSubcontractedEmailOptions.slice(0, 3);
    });
  }, [showNotes4subcontracted, selectedSubcontractedEmailOptions]);

  // 🆕 Build available carriers list with internal option (same as CreateLoad)
  useEffect(() => {
    const cars = carrierClients || [];

    const carrierDisplayName = (c) => {
      const company =
        c?.name || // ✅ API often returns `name`
        c?.companyName ||
        c?.company?.companyName ||
        c?.company?.name ||
        c?.businessName ||
        c?.legalName ||
        c?.tradeName ||
        "";

      const person = [c?.firstName, c?.lastName].filter(Boolean).join(" ").trim();
      return String(company || person || `Carrier #${c?.id || ""}`).trim();
    };

    const carrierSearchText = (c) => {
      const parts = [
        carrierDisplayName(c),
        c?.companyName,
        c?.company?.companyName,
        c?.vatNumber,
        c?.nif,
        c?.email,
        c?.phone,
      ];
      return parts.filter(Boolean).join(" ").toLowerCase();
    };

    const internalOpt =
      companyName && String(companyName).trim()
        ? {
            value: "__internal__",
            label: `${String(companyName).trim()} (internal)`,
            searchText: `${String(companyName).trim()} internal`.toLowerCase(),
            isInternal: true,
          }
        : null;

    setAvailableCarriers([
      ...(internalOpt ? [internalOpt] : []),
      ...cars.map((c) => ({
        value: String(c.id),
        label: carrierDisplayName(c),
        searchText: carrierSearchText(c),
        isInternal: false,
      })),
    ]);
  }, [carrierClients, companyName]);

  // 🆕 auto-select internal carrier by default
  useEffect(() => {
    if (carrierId != null && String(carrierId).trim() !== "") return;

    const internal = (availableCarriers || []).find(
      (c) => c?.isInternal || String(c?.value) === "__internal__"
    );

    if (internal) {
      setCarrierId(String(internal.value));
    }
  }, [availableCarriers, carrierId]);

  const validateDateOnBlur = (value, index, type) => {
    const parsedDate = new Date(value);
    const isValidDate = !isNaN(parsedDate.getTime());

    if (!isValidDate || value.length !== 10) {
      setDateErrorModal({ visible: true, type, index });
    }
  };

  // Fetch Load Bearer details to refresh remaining space
  const fetchLoadBearerDetails = async (loadBearerId) => {
    try {
      const response = await axios.get(`/loads/${loadBearerId}`);
      if (response.status === 200) {
        const loadBearer = {
          ...response.data,
          height: response.data.height ? parseFloat(response.data.height) : 0,
          width: response.data.width ? parseFloat(response.data.width) : 0,
        };

        const stackedResponse = await axios.get(
          `/loads?transportsessionId=${existingTransportSessionId}&associatedLoadBearerId=${loadBearerId}`
        );

        const stackedLoads = stackedResponse.data.filter(
          (load) => load.superImposable && load.associatedLoadBearerId === loadBearerId
        );

        const usedSpace = stackedLoads.reduce((acc, load) => acc + parseFloat(load.length || 0), 0);
        const availableSpace = Number(
          (Math.max(0, parseFloat(loadBearer.length || 0) - usedSpace)).toFixed(2)
        );

        setLoadBearers((prevLoadBearers) =>
          prevLoadBearers.map((lb) => (lb.id === loadBearerId ? { ...lb, remainingSpace: availableSpace } : lb))
        );

        setRemainingSpace(availableSpace);
      }
    } catch (error) {
      console.error("❌ Error fetching Load Bearer details:", error);
    }
  };

  const handleLoadBearerSelection = (event) => {
    const selectedId = Number(event.target.value);
    setSelectedLoadBearer(selectedId);

    const selectedBearer = loadBearers.find((lb) => lb.id === selectedId);
    setRemainingSpace(selectedBearer ? selectedBearer.remainingSpace : null);

    setShowWarning(false);
    setHighlightDropdown(false);

    fetchLoadBearerDetails(selectedId);
  };

  // Blocked interaction without selecting a load bearer
  const handleBlockedInput = () => {
    if (!selectedLoadBearer) {
      setShowWarning(true);
      setHighlightDropdown(true);

      setTimeout(() => {
        setShowWarning(false);
        setHighlightDropdown(false);
      }, 3000);
    }
  };

  // (kept) not used right now, but could be useful later
  const fetchUsedLength = async () => {
    try {
      const response = await axios.get(`/loads?transportsessionId=${existingTransportSessionId}`);
      const loads = response.data;

      const totalUsedLength = loads.reduce(
        (acc, load) => acc + (load.superImposable && load.associatedLoadBearerId ? 0 : parseFloat(load.length || 0)),
        0
      );

      return Math.max(0, maxLength - totalUsedLength);
    } catch (error) {
      console.error("❌ Error fetching loads for validation:", error);
      return maxLength;
    }
  };

  // ---------------------------
  // Dimensions validation
  // ---------------------------
  const getSelectedLoadBearer = () => {
    return loadBearers.find((lb) => Number(lb.id) === Number(selectedLoadBearer));
  };

  const getAvailableLength = () => {
    return remainingSpace !== null ? parseFloat(remainingSpace) : 0;
  };

  const getAvailableHeight = () => {
    const selectedBearer = getSelectedLoadBearer();
    const bearerHeight = selectedBearer && selectedBearer.height !== undefined
      ? parseFloat(selectedBearer.height || 0)
      : 0;

    return Math.max(0, 2.7 - bearerHeight);
  };

  const getAvailableWidth = () => {
    const selectedBearer = getSelectedLoadBearer();
    const bearerWidth = selectedBearer && selectedBearer.width !== undefined
      ? parseFloat(selectedBearer.width || 0)
      : 0;

    return Math.max(0, 2.5 - bearerWidth);
  };

  const getPointDimensionError = (field, value) => {
    const n = Number(value);

    if (value === "" || value == null) return "";
    if (!Number.isFinite(n) || n <= 0) return t(`errors.${field}.invalid`);

    if (field === "length") {
      const availableLength = getAvailableLength();

      if (n > availableLength) {
        return t("errors.length.exceeds", {
          space: availableLength.toFixed(2),
        });
      }

      return "";
    }

    if (field === "height") {
      const availableHeight = getAvailableHeight();

      if (n > availableHeight) {
        return t("errors.height.exceeds", {
          space: availableHeight.toFixed(2),
        });
      }

      return "";
    }

    if (field === "width") {
      const availableWidth = getAvailableWidth();

      if (n > availableWidth) {
        return t("errors.width.exceeds", {
          space: availableWidth.toFixed(2),
        });
      }

      return "";
    }

    return "";
  };

  const calculatePointDimensionTotals = (points = []) => {
    return points.reduce(
      (acc, point) => {
        const length = Number(point.length || 0);
        const width = Number(point.width || 0);
        const height = Number(point.height || 0);

        return {
          length: acc.length + (Number.isFinite(length) ? length : 0),
          width: Math.max(acc.width, Number.isFinite(width) ? width : 0),
          height: Math.max(acc.height, Number.isFinite(height) ? height : 0),
        };
      },
      { length: 0, width: 0, height: 0 }
    );
  };

  const pickupDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(pickUpPlaces);
  }, [pickUpPlaces]);

  const deliveryDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(deliveryPlaces);
  }, [deliveryPlaces]);

  const pointDimensionSummary = useMemo(() => {
    const pickupLength = Number(pickupDimensionTotals.length || 0);
    const deliveryLength = Number(deliveryDimensionTotals.length || 0);

    const lengthMatches =
      Math.round(pickupLength * 100) === Math.round(deliveryLength * 100);

    return {
      length: pickupLength,
      pickupLength,
      deliveryLength,
      width: Math.max(pickupDimensionTotals.width, deliveryDimensionTotals.width),
      height: Math.max(pickupDimensionTotals.height, deliveryDimensionTotals.height),
      lengthMatches,
    };
  }, [pickupDimensionTotals, deliveryDimensionTotals]);

  useEffect(() => {
    const nextLength = pointDimensionSummary.length > 0
      ? pointDimensionSummary.length.toFixed(2)
      : "";

    const nextWidth = pointDimensionSummary.width > 0
      ? pointDimensionSummary.width.toFixed(2)
      : "";

    const nextHeight = pointDimensionSummary.height > 0
      ? pointDimensionSummary.height.toFixed(2)
      : "";

    setLengthInput(nextLength);
    setWidthInput(nextWidth);
    setHeightInput(nextHeight);

    if (nextLength) setLengthError("");
    if (nextWidth) setWidthError("");
    if (nextHeight) setHeightError("");
  }, [
    pointDimensionSummary.length,
    pointDimensionSummary.width,
    pointDimensionSummary.height,
  ]);

  // 🆕 Weight (optional, but validated when provided)
  const handleWeightChange = (event) => {
    if (!selectedLoadBearer) return handleBlockedInput();

    const v = event.target.value;

    // allow empty or 6 digits + optional .xx
    if (/^\d{0,6}(\.\d{0,2})?$/.test(v) || v === "") {
      setWeightInput(v);
    }

    if (v === "") {
      setWeightError("");
      return;
    }

    const n = parseFloat(v);

    if (Number.isNaN(n) || n <= 0) {
      setWeightError(t("errors.weight.invalid"));
    } else {
      setWeightError("");
    }
  };

  // Price (optional, non-negative number with max 2 decimals)
  const handlePriceChange = (event) => {
    const v = event.target.value;
    if (/^\d{0,6}(\.\d{0,2})?$/.test(v) || v === "") {
      setPriceInput(v);
      if (v === "") setPriceError("");
      else {
        const n = Number(v);
        setPriceError(Number.isNaN(n) || n < 0 ? t("errors.price.invalid") : "");
      }
    }
  };

  // 🆕 Price for subcontracted (required when external carrier; non-negative number with max 2 decimals)
  const handlePrice4subcontratedChange = (event) => {
    const v = event.target.value;
    if (/^\d{0,6}(\.\d{0,2})?$/.test(v) || v === "") {
      setPrice4subcontratedInput(v);
      if (v === "") setPrice4subcontratedError("");
      else {
        const n = Number(v);
        setPrice4subcontratedError(Number.isNaN(n) || n < 0 ? t("errors.price.invalid") : "");
      }
    }
  };  

  // Submit
  const handleCreateSuperImposableLoad = async () => {
    setTriedSubmit(true);

    // 🆕 Merchandise (required)
    const cleanMerchandise = String(merchandise || "").trim();

    if (!cleanMerchandise) {
      setMerchandiseError(
        t("errors.merchandise.required", {
          defaultValue: "❌ Merchandise is required.",
        })
      );
      return;
    }

    if (cleanMerchandise.length > 255) {
      setMerchandiseError(t("errors.merchandise.maxChars", { max: 255 }));
      return;
    }

    setMerchandiseError("");

    if (notes4driver && notes4driver.length > 2000) {
      setNotes4driverError(t("errors.notes.maxChars"));
      return;
    }

    if (showNotes4subcontracted && notes4subcontracted && notes4subcontracted.length > 2000) {
      setNotes4subcontractedError(t("errors.notes.maxChars"));
      return;
    }

    // 🆕 Price for subcontracted (optional, but validated when provided)
    if (showNotes4subcontracted) {
      const raw = String(price4subcontratedInput ?? "").trim();

      if (raw) {
        const n = Number(raw);

        if (Number.isNaN(n) || n < 0) {
          setPrice4subcontratedError(t("errors.price.invalid"));
          return;
        }
      }

      setPrice4subcontratedError("");
    }

    // ✅ Total dimensions are auto-calculated from points.
    // Do NOT paint the Merchandise/summary fields red anymore.
    setLengthError("");
    setHeightError("");
    setWidthError("");

    if (
      String(weightInput ?? "").trim() !== "" &&
      (!Number.isFinite(Number(weightInput)) || Number(weightInput) <= 0)
    ) {
      setWeightError(t("errors.weight.invalid"));
      return;
    }

    setWeightError("");

    // Dates are optional. Hours are validated only when provided.
    let hasHourErrors = false;
    const validHourRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

    const updatedPickups = pickUpPlaces.map((place) => {
      const hourError =
        Boolean(String(place.hour || "").trim()) &&
        !validHourRegex.test(String(place.hour || "").trim());

      if (hourError) hasHourErrors = true;

      return {
        ...place,
        dateError: false,
        hourError,
      };
    });

    const updatedDeliveries = deliveryPlaces.map((place) => {
      const hourError =
        Boolean(String(place.hour || "").trim()) &&
        !validHourRegex.test(String(place.hour || "").trim());

      if (hourError) hasHourErrors = true;

      return {
        ...place,
        dateError: false,
        hourError,
      };
    });

    setPickUpPlaces(updatedPickups);
    setDeliveryPlaces(updatedDeliveries);

    if (hasHourErrors) {
      setError(t("errors.hour.globalInvalid"));
      return;
    }

    const hasPickup = pickUpPlaces.some((p) => p.location && p.location.trim() !== "");
    const hasDelivery = deliveryPlaces.some((p) => p.location && p.location.trim() !== "");

    let hasExtraFieldErrors = false;

    const updatedPickupsWithErrors = updatedPickups.map((place) => {
      const locationError = !String(place.location || "").trim();
      const addressError = false;
      const postalCodeError = false;
      const cityError = false;
      const countryError = false;
      const contactPhoneError = false;
      const gMapsLinkError = false;
      const coordinatesError = false;

      const lengthError =
        String(place.length ?? "").trim() !== ""
          ? getPointDimensionError("length", place.length)
          : "";

      const widthError =
        String(place.width ?? "").trim() !== ""
          ? getPointDimensionError("width", place.width)
          : "";

      const heightError =
        String(place.height ?? "").trim() !== ""
          ? getPointDimensionError("height", place.height)
          : "";

      const loadingAccessError = false;
      const handlingEquipmentError = false;

      const handlingEquipmentOtherError =
        String(place.handlingEquipment || "").toUpperCase() === "OTHER" &&
        !String(place.handlingEquipmentOther || "").trim();

      if (
        locationError ||
        addressError ||
        postalCodeError ||
        cityError ||
        countryError ||
        contactPhoneError ||
        gMapsLinkError ||
        coordinatesError ||
        lengthError ||
        widthError ||
        heightError ||
        loadingAccessError ||
        handlingEquipmentError ||
        handlingEquipmentOtherError
      ) {
        hasExtraFieldErrors = true;
      }

      return {
        ...place,
        locationError,
        addressError,
        postalCodeError,
        cityError,
        countryError,
        contactPhoneError,
        gMapsLinkError,
        coordinatesError,
        lengthError,
        widthError,
        heightError,
        loadingAccessError,

        // 🆕
        handlingEquipmentError,
        handlingEquipmentOtherError,

        showMoreFields:
          locationError ||
          addressError ||
          postalCodeError ||
          cityError ||
          countryError ||
          contactPhoneError ||
          gMapsLinkError ||
          coordinatesError
            ? true
            : place.showMoreFields,
      };
    });

    const updatedDeliveriesWithErrors = updatedDeliveries.map((place) => {
      const locationError = !String(place.location || "").trim();
      const addressError = false;
      const postalCodeError = false;
      const cityError = false;
      const countryError = false;
      const contactPhoneError = false;
      const gMapsLinkError = false;
      const coordinatesError = false;

      const lengthError =
        String(place.length ?? "").trim() !== ""
          ? getPointDimensionError("length", place.length)
          : "";

      const widthError =
        String(place.width ?? "").trim() !== ""
          ? getPointDimensionError("width", place.width)
          : "";

      const heightError =
        String(place.height ?? "").trim() !== ""
          ? getPointDimensionError("height", place.height)
          : "";

      const loadingAccessError = false;
      const handlingEquipmentError = false;

      const handlingEquipmentOtherError =
        String(place.handlingEquipment || "").toUpperCase() === "OTHER" &&
        !String(place.handlingEquipmentOther || "").trim();

      if (
        locationError ||
        addressError ||
        postalCodeError ||
        cityError ||
        countryError ||
        contactPhoneError ||
        gMapsLinkError ||
        coordinatesError ||
        lengthError ||
        widthError ||
        heightError ||
        loadingAccessError ||
        handlingEquipmentError ||
        handlingEquipmentOtherError
      ) {
        hasExtraFieldErrors = true;
      }

      return {
        ...place,
        locationError,
        addressError,
        postalCodeError,
        cityError,
        countryError,
        contactPhoneError,
        gMapsLinkError,
        coordinatesError,
        lengthError,
        widthError,
        heightError,

        // 🆕
        loadingAccessError,
        handlingEquipmentError,
        handlingEquipmentOtherError,

        showMoreFields:
          locationError ||
          addressError ||
          postalCodeError ||
          cityError ||
          countryError ||
          contactPhoneError ||
          gMapsLinkError ||
          coordinatesError
            ? true
            : place.showMoreFields,
      };
    });

    setPickUpPlaces(updatedPickupsWithErrors);
    setDeliveryPlaces(updatedDeliveriesWithErrors);

    if (!hasPickup) setPickUpPlaceError(t("errors.location.required"));
    else setPickUpPlaceError("");

    if (!hasDelivery) setDeliveryPlaceError(t("errors.location.required"));
    else setDeliveryPlaceError("");

    if (!hasPickup || !hasDelivery || hasExtraFieldErrors) {
      return;
    }

    const toCents = (value) => Math.round(Number(value || 0) * 100);

    const pickupLengthTotal = updatedPickupsWithErrors.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const deliveryLengthTotal = updatedDeliveriesWithErrors.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const allPickupLengthsProvided =
      updatedPickupsWithErrors.length > 0 &&
      updatedPickupsWithErrors.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    const allDeliveryLengthsProvided =
      updatedDeliveriesWithErrors.length > 0 &&
      updatedDeliveriesWithErrors.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    if (
      allPickupLengthsProvided &&
      allDeliveryLengthsProvided &&
      toCents(pickupLengthTotal) !== toCents(deliveryLengthTotal)
    ) {
      const mismatchMessage = t("errors.pickupDeliveryLengthMismatch", {
        defaultValue: "❌ The total pickup length must match the total delivery length.",
      });

      setPickUpPlaceError(mismatchMessage);
      setDeliveryPlaceError(mismatchMessage);

      setPickUpPlaces(
        updatedPickupsWithErrors.map((place) => ({
          ...place,
          lengthError: place.lengthError || t("errors.length.mismatch", {
            defaultValue: "❌ Pickup and delivery length totals must match.",
          }),
        }))
      );

      setDeliveryPlaces(
        updatedDeliveriesWithErrors.map((place) => ({
          ...place,
          lengthError: place.lengthError || t("errors.length.mismatch", {
            defaultValue: "❌ Pickup and delivery length totals must match.",
          }),
        }))
      );

      setError(mismatchMessage);
      return;
    }

    if (Number(lengthInput || 0) > getAvailableLength()) {
      setError(t("errors.space.cannotStack", { space: getAvailableLength().toFixed(2) }));
      return;
    }

    if (lengthError || heightError || widthError || weightError) {
      setError(t("errors.fixAllBeforeSubmit"));
      return;
    }

    if (!selectedLoadBearer) {
      setError(t("errors.loadBearer.required"));
      return;
    }

    if (remainingSpace === null) {
      setError(t("errors.space.fetchFailed"));
      return;
    }

    const inputLength = parseFloat(lengthInput);
    const preciseAvailableSpace = parseFloat(remainingSpace.toFixed(2));

    if (
      Number.isFinite(inputLength) &&
      inputLength > preciseAvailableSpace
    ) {
      setError(t("errors.space.cannotStack", { space: preciseAvailableSpace.toFixed(2) }));
      return;
    }

    const hasInvalidPickupDeliveryOrder = updatedDeliveriesWithErrors.some(
      (delivery) => {
        if (!delivery.date || !delivery.hour) return false;

        const deliveryDateTime = new Date(
          `${delivery.date}T${delivery.hour}`
        );

        if (Number.isNaN(deliveryDateTime.getTime())) return false;

        return updatedPickupsWithErrors.some((pickup) => {
          if (!pickup.date || !pickup.hour) return false;

          const pickupDateTime = new Date(
            `${pickup.date}T${pickup.hour}`
          );

          if (Number.isNaN(pickupDateTime.getTime())) return false;

          return deliveryDateTime < pickupDateTime;
        });
      }
    );

    if (hasInvalidPickupDeliveryOrder) {
      setError(t("errors.deliveryBeforePickup"));
      return;
    }

    const priceValue =
      String(priceInput ?? "").trim() === ""
        ? null
        : Number(priceInput);

    if (priceValue === null) {
      setPriceError(
        t("errors.price.required", {
          defaultValue: "❌ Price is required.",
        })
      );
      setError(
        t("errors.price.required", {
          defaultValue: "❌ Price is required.",
        })
      );
      return;
    }

    if (!Number.isFinite(priceValue) || priceValue < 0) {
      setPriceError(t("errors.price.invalid"));
      setError(t("errors.price.invalid"));
      return;
    }

    setPriceError("");

    if (!clientType) {
      setError(
        t("errors.clientType.required", {
          defaultValue: "❌ Client type is required.",
        })
      );
      return;
    }

    const selectedClientId =
      clientType === "industry"
        ? industryClientId
        : carrierClientId;

    if (!selectedClientId) {
      setError(
        t("errors.client.required", {
          defaultValue: "❌ Client is required.",
        })
      );
      return;
    }

    // 🆕 Trailer Type (optional)
    const tt = String(trailerType || "").trim().toUpperCase();
    const ttOther = String(trailerTypeOther || "").trim();

    setTrailerTypeError(false);

    if (tt === "OTHER" && !ttOther) {
      setTrailerTypeOtherError(true);
      setError(
        t("trailerType.errors.otherRequired", { defaultValue: "Please specify the trailer type." })
      );
      return;
    }
    setTrailerTypeOtherError(false);

    const cleanNotes4driver = typeof notes4driver === "string" ? (notes4driver.trim() || null) : null;

    const cleanNotes4subcontracted =
      showNotes4subcontracted && typeof notes4subcontracted === "string"
        ? (notes4subcontracted.trim() || null)
        : null;

    const selectedTransportOpt = (availableCarriers || []).find(
      (c) => String(c.value) === String(carrierId)
    );

    const isInternalCarrier = selectedTransportOpt?.value === "__internal__";

    const carrierIdPayload =
      isInternalCarrier ? null : (carrierId ? Number(carrierId) : null);

    const carrierNamePayload = isInternalCarrier
      ? (companyName || null)
      : (selectedTransportOpt?.label || null);

    const payload = {
      transportsessionId: existingTransportSessionId,
      length: Number.isFinite(inputLength)
        ? parseFloat(inputLength.toFixed(2))
        : null,

      height:
        String(heightInput ?? "").trim() !== "" &&
        Number.isFinite(Number(heightInput))
          ? Number(heightInput)
          : null,

      width:
        String(widthInput ?? "").trim() !== "" &&
        Number.isFinite(Number(widthInput))
          ? Number(widthInput)
          : null,

      // 🆕 Weight
      weight:
        String(weightInput ?? "").trim() !== "" &&
        Number.isFinite(Number(weightInput))
          ? parseFloat(Number(weightInput).toFixed(2))
          : null,

      merchandise: String(merchandise || "").trim() || null,

      price: parseFloat(priceValue.toFixed(2)),
      price4subcontrated:
        showNotes4subcontracted &&
        String(price4subcontratedInput ?? "").trim() !== ""
          ? parseFloat(Number(price4subcontratedInput).toFixed(2))
          : null,

      notes4driver: cleanNotes4driver,
      notes4subcontracted: cleanNotes4subcontracted,

      // carrier info (same concept as CreateLoad)
      carrierId: carrierIdPayload,
      carrierName: carrierNamePayload,

      superImposable: true,
      associatedLoadBearerId: Number(selectedLoadBearer),

      // 🆕 Trailer type
      trailerType: String(trailerType || "").trim() ? String(trailerType).trim().toUpperCase() : null,
      trailerTypeOther:
        String(trailerType || "").trim().toUpperCase() === "OTHER"
          ? (String(trailerTypeOther || "").trim() || null)
          : null,

      pickupLocations: updatedPickupsWithErrors
        .filter((place) => place.location.trim() !== "")
        .map((place) => ({
          location: place.location,
          date: place.date || null,
          hour: place.hour || null,

          // 🆕 point-level dimensions
          length: place.length === "" ? null : Number(place.length),
          width: place.width === "" ? null : Number(place.width),
          height: place.height === "" ? null : Number(place.height),

          placeId: place.placeId || null,
          address: place.address,
          postalCode: place.postalCode,
          city: place.city,
          country: place.country,
          gMapsLink: place.gMapsLink,
          coordinates: place.coordinates,
          contactPhone: place.contactPhone,
          loadingAccess: place.loadingAccess || null,

          handlingEquipment: place.handlingEquipment || null,
          handlingEquipmentOther:
            String(place.handlingEquipment || "").toUpperCase() === "OTHER"
              ? (String(place.handlingEquipmentOther || "").trim() || null)
              : null,

          // 🆕 reference (optional)
          reference: String(place.reference || "").trim() || null,
        })),

      deliveryLocations: updatedDeliveriesWithErrors
        .filter((place) => place.location.trim() !== "")
        .map((place) => ({
          location: place.location,
          date: place.date,
          hour: place.hour || null,

          // 🆕 point-level dimensions
          length: place.length === "" ? null : Number(place.length),
          width: place.width === "" ? null : Number(place.width),
          height: place.height === "" ? null : Number(place.height),

          placeId: place.placeId || null,
          address: place.address,
          postalCode: place.postalCode,
          city: place.city,
          country: place.country,
          gMapsLink: place.gMapsLink,
          coordinates: place.coordinates,
          contactPhone: place.contactPhone,
          loadingAccess: place.loadingAccess || null,

          handlingEquipment: place.handlingEquipment || null,
          handlingEquipmentOther:
            String(place.handlingEquipment || "").toUpperCase() === "OTHER"
              ? (String(place.handlingEquipmentOther || "").trim() || null)
              : null,

          // 🆕 reference (optional)
          reference: String(place.reference || "").trim() || null,
        })),

      industryClientId: industryClientId || null,
      carrierClientId: carrierClientId || null,
      clientType: clientType || null,
      clientContactEmails: selectedClientContactEmails.map((opt) => opt.value),
      subcontractedEmails: showNotes4subcontracted
        ? selectedSubcontractedEmails.map((opt) => opt.value).slice(0, 3)
        : [],
    };

    setLoading(true);
    setMessage("");
    setError("");

    try {
      const response = await axios.post("/loads/create", payload);

      if (response.status === 201) {
        setCreatedLoadId(response.data?.load?.id ?? null);
        setSuccessPopupOpen(true);
        fetchLoadBearerDetails(selectedLoadBearer);
      } else {
        setMessage(t("errors.create.failed"));
      }
    } catch (err) {
      console.error("❌ Error creating Load:", err);

      if (err?.response) {
        setError(
          t("errors.create.backend", {
            msg: err.response.data?.error || t("errors.create.generic"),
          })
        );
      } else if (err?.request) {
        setError(t("errors.create.noResponse"));
      } else {
        setError(t("errors.create.backend", { msg: err?.message || t("errors.create.generic") }));
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // Live field changes
  // ---------------------------
  const handlePickUpPlaceChange = (event, index, field) => {
    const updated = [...pickUpPlaces];
    const value = event.target.value;

    updated[index][field] = value;

    if (["length", "width", "height"].includes(field)) {
      updated[index][`${field}Error`] = getPointDimensionError(field, value);
    } else if (field === "date") updated[index].dateError = false;
    else if (field === "location") updated[index].locationError = !value.trim();
    else if (field === "address") updated[index].addressError = !value.trim();
    else if (field === "postalCode") updated[index].postalCodeError = !value.trim();
    else if (field === "city") updated[index].cityError = !value.trim();
    else if (field === "country") updated[index].countryError = !value.trim();
    else if (field === "contactPhone") updated[index].contactPhoneError = !value.trim();
    else if (field === "gMapsLink") updated[index].gMapsLinkError = !value.trim();
    else if (field === "coordinates") updated[index].coordinatesError = !value.trim();
    else if (field === "reference") {
      // optional -> no error
    }
    else if (field === "handlingEquipmentOther") {
      updated[index].handlingEquipmentOtherError =
        String(updated[index].handlingEquipment || "").toUpperCase() === "OTHER" && !value.trim();
    }

    setPickUpPlaces(updated);
  };

  const addPickUpPlace = () => {
    if (pickUpPlaces.length >= MAX_POINTS) return;
    setPickUpPlaces([
      ...pickUpPlaces,
      {
        location: "",
        date: "",
        hour: "",
        hourError: false,
        dateError: false,

        // 🆕 point-level dimensions
        length: "",
        width: "",
        height: "",
        lengthError: "",
        widthError: "",
        heightError: "",

        placeId: null,
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",
        loadingAccess: "SIDE",
        loadingAccessError: false,

        handlingEquipment: "FORKLIFT",
        handlingEquipmentError: false,
        handlingEquipmentOther: "",
        handlingEquipmentOtherError: false,

        // 🆕 Reference (optional)
        reference: "",

        showMoreFields: false,
      },
    ]);
  };

  const removePickUpPlace = (index) => {
    const updatedPlaces = pickUpPlaces.filter((_, i) => i !== index);
    setPickUpPlaces(updatedPlaces);
  };

  const handleDeliveryPlaceChange = (event, index, field) => {
    const updated = [...deliveryPlaces];
    const value = event.target.value;

    updated[index][field] = value;

    if (["length", "width", "height"].includes(field)) {
      updated[index][`${field}Error`] = getPointDimensionError(field, value);
    } else if (field === "date") updated[index].dateError = false;
    else if (field === "address") updated[index].addressError = !value.trim();
    else if (field === "postalCode") updated[index].postalCodeError = !value.trim();
    else if (field === "city") updated[index].cityError = !value.trim();
    else if (field === "country") updated[index].countryError = !value.trim();
    else if (field === "contactPhone") updated[index].contactPhoneError = !value.trim();
    else if (field === "gMapsLink") updated[index].gMapsLinkError = !value.trim();
    else if (field === "coordinates") updated[index].coordinatesError = !value.trim();
    else if (field === "reference") {
      // optional -> no error
    }
    else if (field === "location") updated[index].locationError = !value.trim();
    else if (field === "handlingEquipmentOther") {
      updated[index].handlingEquipmentOtherError =
        String(updated[index].handlingEquipment || "").toUpperCase() === "OTHER" && !value.trim();
    }

    setDeliveryPlaces(updated);
  };

  const addDeliveryPlace = () => {
    if (deliveryPlaces.length >= MAX_POINTS) return;
    setDeliveryPlaces([
      ...deliveryPlaces,
      {
        location: "",
        date: "",
        hour: "",
        hourError: false,
        dateError: false,

        // 🆕 point-level dimensions
        length: "",
        width: "",
        height: "",
        lengthError: "",
        widthError: "",
        heightError: "",

        placeId: null,
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",
        loadingAccess: "SIDE",
        loadingAccessError: false,

        handlingEquipment: "FORKLIFT",
        handlingEquipmentError: false,
        handlingEquipmentOther: "",
        handlingEquipmentOtherError: false,

        // 🆕 Reference (optional)
        reference: "",

        showMoreFields: false,
      },
    ]);
  };

  const removeDeliveryPlace = (index) => {
    const updatedPlaces = deliveryPlaces.filter((_, i) => i !== index);
    setDeliveryPlaces(updatedPlaces);
  };

  const handleHourBlur = () => {
    const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    let hasInvalid = false;

    const validateHours = (places, setPlaces) => {
      const updated = places.map((p) => {
        const hour = p.hour || "";
        const isValid = regex.test(hour);
        const hourError = hour.length > 0 && !isValid;
        if (hourError) hasInvalid = true;
        return { ...p, hourError };
      });
      setPlaces(updated);
    };

    validateHours(pickUpPlaces, setPickUpPlaces);
    validateHours(deliveryPlaces, setDeliveryPlaces);
    setGlobalHourFormatError(hasInvalid);
  };

  const handleLoadingAccessChange = (index, opt, isDelivery) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    const nextErr = triedSubmit ? !value : false; // ✅ só mostra erro após submit

    if (isDelivery) {
      setDeliveryPlaces((prev) =>
        prev.map((p, i) =>
          i === index
            ? { ...p, loadingAccess: value, loadingAccessError: nextErr }
            : p
        )
      );
    } else {
      setPickUpPlaces((prev) =>
        prev.map((p, i) =>
          i === index
            ? { ...p, loadingAccess: value, loadingAccessError: nextErr }
            : p
        )
      );
    }
  };

  const handleHandlingEquipmentChange = (index, opt, isDelivery) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    const patch = (p) => {
      const isOther = value === "OTHER";
      const nextOther = isOther ? (p.handlingEquipmentOther || "") : "";
      return {
        ...p,
        handlingEquipment: value,
        handlingEquipmentError: !value,
        handlingEquipmentOther: nextOther,
        handlingEquipmentOtherError: isOther ? !String(nextOther || "").trim() : false,
      };
    };

    if (isDelivery) {
      setDeliveryPlaces((prev) => prev.map((p, i) => (i === index ? patch(p) : p)));
    } else {
      setPickUpPlaces((prev) => prev.map((p, i) => (i === index ? patch(p) : p)));
    }
  };

  const handleTrailerTypeChange = (opt) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    setTrailerType(value);

    // ✅ só mostra erro após submit
    setTrailerTypeError(triedSubmit ? !value : false);

    const isOther = value === "OTHER";

    if (!isOther) {
      setTrailerTypeOther("");
      setTrailerTypeOtherError(false);
    } else {
      // ✅ só valida "Other" após submit (e depois vai limpando em live)
      setTrailerTypeOtherError(triedSubmit ? !String(trailerTypeOther || "").trim() : false);
    }
  };

  return (
    <div className="superimposable-load-container">
      <div
        className="superimposable-load-back-arrow"
        onClick={() => navigate("/loads")}
      >
        ← {t("back")}
      </div>

      {successPopupOpen && (
        <div
          className="superimposable-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="superimposable-success-title"
        >
          <div className="superimposable-popupModal">
            <div className="superimposable-popupIconWrap">
              <FaCheckCircle className="superimposable-popupIcon" />
            </div>

            <h2
              id="superimposable-success-title"
              className="superimposable-popupTitle"
            >
              {t("popup.created_title")}
            </h2>

            <p className="superimposable-popupMessage">
              {t("popup.created_message", { id: createdLoadId || "—" })}
            </p>

            <div className="superimposable-popupActions">
              <button
                type="button"
                className="superimposable-popupPrimaryBtn"
                onClick={handleCloseSuccessPopup}
              >
                {t("popup.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="superimposable-load-title">{t("title")}</h2>

      <div className="superimposable-load-form">
        {/* Line 1: Load Bearer Selection */}
        <div className="superimposable-select-load-bearer-row">
          <div className="superimposable-field-group">
            <label className="superimposable-label">{t("labels.selectLoadBearer")}*</label>

            <select
              value={selectedLoadBearer || ""}
              onChange={handleLoadBearerSelection}
              className={`superimposable-input ${highlightDropdown ? "highlight" : ""}`}
            >
              <option value="" disabled hidden>
                {t("placeholders.selectLoadBearer")}
              </option>

              {loadBearers.length > 0 ? (
                loadBearers.map((load) => (
                  <option key={load.id} value={load.id} disabled={load.remainingSpace === 0}>
                    {t("loadBearer.option", {
                      id: load.id,
                      space: load.remainingSpace.toFixed(2),
                    })}
                  </option>
                ))
              ) : (
                <option disabled>{t("loadBearer.none")}</option>
              )}
            </select>
          </div>
        </div>

        {showWarning && <p className="superimposable-error-text">{t("warnings.selectLoadBearerFirst")}</p>}

        {/* Parent Wrapper for Columns */}
        <div className="superimposable-columns">
          {/* Column 1: Loading Points */}
          <div className="superimposable-column">
            <h3 className="superimposable-column-title">{t("sections.loadingPoints")}</h3>

            {pickUpPlaces.map((place, index) => (
              <div key={index} className="multi-location-input">
                <div className="superimposable-pickup-label-row">
                  <label className="superimposable-label">{t("labels.pickupPlace")}*</label>

                  {pickUpPlaces.length > 1 && (
                    <button
                      type="button"
                      className="superimposable-delete-pickup-btn"
                      onClick={() => removePickUpPlace(index)}
                      title={t("tooltips.removePickup")}
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="select-wrapper-left-icon">
                  <CreatableSelect
                    isClearable
                    isMulti={false}
                    options={placeOptions}
                    placeholder={t("placeholders.selectOrTypePlace")}
                    value={
                      place.placeId
                        ? placeOptions.find((p) => p.value === place.placeId) || null
                        : place.location
                        ? { label: place.location, value: place.location }
                        : null
                    }
                    onMenuOpen={() => {
                      if (!selectedLoadBearer) handleBlockedInput();
                    }}
                    onFocus={() => {
                      if (!selectedLoadBearer) handleBlockedInput();
                    }}
                    onChange={(selectedOption) => {
                      const updated = [...pickUpPlaces];

                      if (selectedOption?.__isNew__) {
                        updated[index] = {
                          ...updated[index],
                          placeId: null,
                          location: selectedOption.label,
                          address: "",
                          postalCode: "",
                          city: "",
                          country: "",
                          gMapsLink: "",
                          coordinates: "",
                          contactPhone: "",
                          loadingAccess: "SIDE",
                          loadingAccessError: false,

                          handlingEquipment: "FORKLIFT",
                          handlingEquipmentError: false,
                          handlingEquipmentOther: "",
                          handlingEquipmentOtherError: false,

                          // 🆕 reference (optional)
                          reference: "",
                        };
                      } else if (selectedOption?.fullData) {
                        const p = selectedOption.fullData;
                        updated[index] = {
                          ...updated[index],
                          placeId: p.id,
                          location: p.placeName,
                          address: p.address,
                          postalCode: p.postalCode,
                          city: p.city,
                          country: p.country,
                          gMapsLink: p.gMapsLink,
                          coordinates: p.coordinates,
                          contactPhone: p.contactPhone,
                          // 🆕 autofill
                          loadingAccess: p.loadingAccess || "SIDE",
                          loadingAccessError: false,

                          // 🆕 Handling equipment
                          handlingEquipment: p.handlingEquipment || "FORKLIFT",
                          handlingEquipmentError: false,
                          handlingEquipmentOther: p.handlingEquipmentOther || "",
                          handlingEquipmentOtherError: false,

                          // 🆕 reference (optional)
                          reference: "",
                        };
                      } else {
                        updated[index] = {
                          ...updated[index],
                          placeId: null,
                          location: selectedOption?.label || "",
                          address: "",
                          postalCode: "",
                          city: "",
                          country: "",
                          gMapsLink: "",
                          coordinates: "",
                          contactPhone: "",
                          loadingAccess: "SIDE",
                          loadingAccessError: false,

                          handlingEquipment: "FORKLIFT",
                          handlingEquipmentError: false,
                          handlingEquipmentOther: "",
                          handlingEquipmentOtherError: false,
                        };
                      }

                      // clear required error instantly
                      updated[index].locationError = !updated[index].location?.trim();
                      setPickUpPlaces(updated);
                    }}
                    styles={customSelectStyles}
                    classNamePrefix={`superimposable-select${place.locationError ? "-error" : ""}`}
                  />
                </div>

                {place.locationError && <p className="error-text">❌ {t("errors.location.required")}</p>}

                {/* Date + Hour */}
                <div className="input-row tighter-row">
                  <div className="custom-input-wrapper">
                    <input
                      type="date"
                      value={place.date || ""}
                      onChange={(e) => handlePickUpPlaceChange(e, index, "date")}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value.length === 10) validateDateOnBlur(value, index, "pickup");
                      }}
                      onFocus={handleBlockedInput}
                      className={`superimposable-input ${place.dateError ? "input-error" : ""}`}
                      ref={(el) => {
                        if (el) dateRefs.current[index] = el;
                      }}
                    />

                    <FaCalendarAlt
                      className="input-icon clickable"
                      onClick={(e) => {
                        e.preventDefault();
                        const input = dateRefs.current[index];
                        if (input && typeof input.showPicker === "function") {
                          input.focus();
                          setTimeout(() => input.showPicker(), 0);
                        }
                      }}
                    />
                  </div>

                  <div className="custom-input-wrapper">
                    <input
                      type="text"
                      placeholder={t("placeholders.hhmm")}
                      value={place.hour || ""}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, "");
                        let formatted = raw;
                        if (raw.length >= 3) formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;

                        const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                        const isValid = regex.test(formatted);

                        const updated = [...pickUpPlaces];
                        updated[index].hour = formatted;
                        updated[index].hourError = formatted !== "" && !isValid;
                        setPickUpPlaces(updated);
                      }}
                      onBlur={() => {
                        const updated = [...pickUpPlaces];
                        const hour = updated[index].hour;
                        const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                        updated[index].hourError = hour && !regex.test(hour);
                        setPickUpPlaces(updated);
                        handleHourBlur();
                      }}
                      readOnly={!selectedLoadBearer}
                      onFocus={handleBlockedInput}
                      className={`superimposable-input ${place.hourError ? "input-error" : ""}`}
                      ref={(el) => {
                        if (el) hourRefs.current[index] = el;
                      }}
                    />

                    <FaClock
                      className="input-icon clickable"
                      onClick={(e) => {
                        e.preventDefault();
                        const input = hourRefs.current[index];
                        if (input && !input.readOnly && typeof input.showPicker === "function") {
                          input.focus();
                          input.showPicker();
                        }
                      }}
                    />
                  </div>
                </div>

                {place.dateError && <p className="error-text">❌ {t("errors.date.required")}</p>}

                {place.hourError && (
                  <p className="error-text">
                    ❌{" "}
                    {place.hour
                      ? t("errors.hour.invalidFormat")
                      : t("errors.hour.required")}
                  </p>
                )}

                <div className="superimposable-point-dimensions-block">
                  <label className="superimposable-label superimposable-point-dimensions-title">
                    {t("labels.pointDimensions", {
                      defaultValue: "Load dimensions at this point (in meters)",
                    })}
                  </label>

                  <div className="superimposable-point-dimensions-row">
                    <div className="superimposable-point-dimension-field">
                      <label>{t("labels.pointLengthShort", { defaultValue: "Length" })}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={place.length || ""}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "length")}
                        onFocus={handleBlockedInput}
                        readOnly={!selectedLoadBearer}
                        className={`superimposable-input ${place.lengthError ? "input-error" : ""}`}
                        placeholder="0.00m"
                      />
                      {place.lengthError && <p className="error-text">{place.lengthError}</p>}
                    </div>

                    <div className="superimposable-point-dimension-field">
                      <label>{t("labels.pointWidthShort", { defaultValue: "Width" })}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={place.width || ""}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "width")}
                        onFocus={handleBlockedInput}
                        readOnly={!selectedLoadBearer}
                        className={`superimposable-input ${place.widthError ? "input-error" : ""}`}
                        placeholder="0.00m"
                      />
                      {place.widthError && <p className="error-text">{place.widthError}</p>}
                    </div>

                    <div className="superimposable-point-dimension-field">
                      <label>{t("labels.pointHeightShort", { defaultValue: "Height" })}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={place.height || ""}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "height")}
                        onFocus={handleBlockedInput}
                        readOnly={!selectedLoadBearer}
                        className={`superimposable-input ${place.heightError ? "input-error" : ""}`}
                        placeholder="0.00m"
                      />
                      {place.heightError && <p className="error-text">{place.heightError}</p>}
                    </div>
                  </div>
                </div>

                {/* 🆕 Loading Access */}
                <label className="superimposable-label">
                  {t("loadingAccess.label", { defaultValue: "Loading access" })}
                </label>

                <div className="superimposable-select-wrapper" style={{ marginTop: -6, marginBottom: 5 }}>
                  <Select
                    options={loadingAccessOptions}
                    value={
                      loadingAccessOptions.find(
                        (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                      ) || null
                    }
                    onChange={(opt) => handleLoadingAccessChange(index, opt, false)}
                    isClearable
                    placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                    styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    classNamePrefix={`superimposable-select${(triedSubmit && place.loadingAccessError) ? "-error" : ""}`}
                    menuPortalTarget={document.body}
                  />
                </div>

                {triedSubmit && place.loadingAccessError && (
                  <p className="error-text">❌ {t("loadingAccess.errors.required", { defaultValue: "Loading access is required." })}</p>
                )}

                {/* 🆕 Handling Equipment */}
                <label className="superimposable-label">
                  {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                </label>

                <div className="superimposable-select-wrapper" style={{ marginTop: -6, marginBottom: 10 }}>
                  <Select
                    options={handlingEquipmentOptions}
                    value={
                      handlingEquipmentOptions.find(
                        (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                      ) || null
                    }
                    onChange={(opt) => handleHandlingEquipmentChange(index, opt, false)}
                    isClearable
                    placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select handling equipment..." })}
                    styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    classNamePrefix={`superimposable-select${place.handlingEquipmentError ? "-error" : ""}`}
                    menuPortalTarget={document.body}
                  />
                </div>

                {place.handlingEquipmentError && (
                  <p className="error-text">❌ {t("handlingEquipment.errors.required", { defaultValue: "Handling equipment is required." })}</p>
                )}

                {String(place.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                  <div className="superimposable-field-group" style={{ marginTop: -10, marginBottom: 10 }}>
                    <label className="superimposable-label">
                      {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                    </label>

                    <input
                      type="text"
                      placeholder={t("fields.placeholders.handlingEquipmentOther", { defaultValue: "Write the equipment..." })}
                      value={place.handlingEquipmentOther || ""}
                      onChange={(e) => handlePickUpPlaceChange(e, index, "handlingEquipmentOther")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.handlingEquipmentOtherError ? "input-error" : ""}`}
                    />

                    {place.handlingEquipmentOtherError && (
                      <p className="error-text">
                        ❌ {t("fields.errors.handlingEquipmentOtherRequired", { defaultValue: "Other handling equipment is required." })}
                      </p>
                    )}
                  </div>
                )}

                {/* 🆕 Reference (optional) */}
                <div className="superimposable-field-group" style={{ marginTop: -6, marginBottom: 10 }}>
                  <label className="superimposable-label">{t("labels.reference", { defaultValue: "Reference" })}</label>

                  <input
                    type="text"
                    placeholder={t("placeholders.reference", { defaultValue: "e.g.: PO 12345, customer ref..." })}
                    value={place.reference || ""}
                    onChange={(e) => handlePickUpPlaceChange(e, index, "reference")}
                    onFocus={handleBlockedInput}
                    readOnly={!selectedLoadBearer}
                    className="superimposable-input"
                  />
                </div>

                {/* Extra fields */}
                {place.showMoreFields ? (
                  <>
                    <input
                      type="text"
                      placeholder={t("placeholders.address")}
                      value={place.address || ""}
                      onChange={(e) => handlePickUpPlaceChange(e, index, "address")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.addressError ? "input-error" : ""}`}
                    />
                    {place.addressError && <p className="error-text">❌ {t("errors.address.required")}</p>}

                    <div className="superimposable-row">
                      <input
                        type="text"
                        placeholder={t("placeholders.postalCode")}
                        value={place.postalCode || ""}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "postalCode")}
                        onFocus={handleBlockedInput}
                        readOnly={!selectedLoadBearer}
                        className={`superimposable-input ${place.postalCodeError ? "input-error" : ""}`}
                      />
                      {place.postalCodeError && <p className="error-text">❌ {t("errors.postalCode.required")}</p>}

                      <input
                        type="text"
                        placeholder={t("placeholders.city")}
                        value={place.city || ""}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "city")}
                        onFocus={handleBlockedInput}
                        readOnly={!selectedLoadBearer}
                        className={`superimposable-input ${place.cityError ? "input-error" : ""}`}
                      />
                      {place.cityError && <p className="error-text">❌ {t("errors.city.required")}</p>}

                      <input
                        type="text"
                        placeholder={t("placeholders.country")}
                        value={place.country || ""}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "country")}
                        onFocus={handleBlockedInput}
                        readOnly={!selectedLoadBearer}
                        className={`superimposable-input ${place.countryError ? "input-error" : ""}`}
                      />
                      {place.countryError && <p className="error-text">❌ {t("errors.country.required")}</p>}
                    </div>

                    <input
                      type="text"
                      placeholder={t("placeholders.contactPhone")}
                      value={place.contactPhone || ""}
                      onChange={(e) => handlePickUpPlaceChange(e, index, "contactPhone")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.contactPhoneError ? "input-error" : ""}`}
                    />
                    {place.contactPhoneError && <p className="error-text">❌ {t("errors.contactPhone.required")}</p>}

                    <input
                      type="text"
                      placeholder={t("placeholders.gMapsLink")}
                      value={place.gMapsLink || ""}
                      onChange={(e) => handlePickUpPlaceChange(e, index, "gMapsLink")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.gMapsLinkError ? "input-error" : ""}`}
                    />
                    {place.gMapsLinkError && <p className="error-text">❌ {t("errors.gMapsLink.required")}</p>}

                    <input
                      type="text"
                      placeholder={t("placeholders.coordinates")}
                      value={place.coordinates || ""}
                      onChange={(e) => handlePickUpPlaceChange(e, index, "coordinates")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.coordinatesError ? "input-error" : ""}`}
                    />
                    {place.coordinatesError && <p className="error-text">❌ {t("errors.coordinates.required")}</p>}

                    <div className="toggle-fields-wrapper">
                      <button
                        type="button"
                        className="superimposable-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...pickUpPlaces];
                          updated[index].showMoreFields = false;
                          setPickUpPlaces(updated);
                        }}
                      >
                        {t("buttons.hideFields")}
                        <FiChevronUp className="superimposable-toggle-arrow" />
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="toggle-fields-wrapper">
                    <button
                      type="button"
                      className="superimposable-toggle-fields-btn"
                      onClick={() => {
                        const updated = [...pickUpPlaces];
                        updated[index].showMoreFields = true;
                        setPickUpPlaces(updated);
                      }}
                    >
                      {t("buttons.moreFieldsRequired")}
                      <FiChevronDown className="superimposable-toggle-arrow" />
                    </button>
                  </div>
                )}
              </div>
            ))}

            {pickUpPlaces.length < MAX_POINTS && (
              <button type="button" className="superimposable-add-btn" onClick={addPickUpPlace}>
                {t("buttons.addAnotherPickup")}
              </button>
            )}

            {pickUpPlaces.length >= MAX_POINTS && (
              <p className="superimposable-error-text-max-points">{t("warnings.maxPickupsReached")}</p>
            )}

            {pickUpPlaceError && <p className="error-text">{pickUpPlaceError}</p>}
          </div>

          {/* Column 2: Merchandise & General Information */}
          <div className="superimposable-column">
            <h3 className="superimposable-column-title">{t("sections.merchandise")}</h3>

            <div className="superimposable-merchandise-inline-row">
              <div className="superimposable-field-group superimposable-weight-field">
                <label className="superimposable-label">{t("labels.weight")}</label>
                <div className="superimposable-input-container">
                  <input
                    type="number"
                    value={weightInput}
                    onChange={handleWeightChange}
                    onFocus={handleBlockedInput}
                    className={`superimposable-input ${weightError ? "input-error" : ""}`}
                    readOnly={!selectedLoadBearer}
                    placeholder="0"
                  />
                </div>
                {weightError && <p className="error-text">{weightError}</p>}
              </div>

              <div className="superimposable-field-group superimposable-merchandise-field">
                <label className="superimposable-label">{t("labels.merchandise")}</label>

                <div className="superimposable-input-container">
                  <input
                    type="text"
                    value={merchandise}
                    onChange={(e) => {
                      const v = e.target.value ?? "";
                      setMerchandise(v);

                      if (v.length > 255) setMerchandiseError(t("errors.merchandise.maxChars", { max: 255 }));
                      else if (merchandiseError) setMerchandiseError("");
                    }}
                    onFocus={handleBlockedInput}
                    readOnly={!selectedLoadBearer}
                    className={`superimposable-input ${merchandiseError ? "input-error" : ""}`}
                    placeholder={t("placeholders.merchandise")}
                  />
                </div>

                {merchandiseError && <p className="error-text">{merchandiseError}</p>}
              </div>
            </div>

            <div className="superimposable-load-dimensions-summary">
              <label className="superimposable-label superimposable-load-dimensions-title">
                {t("labels.loadDimensions", {
                  defaultValue: "Total load dimensions",
                })}
              </label>

              <div className="superimposable-load-dimensions-row">
                <div className="superimposable-load-dimension-field">
                  <label>{t("labels.length", { defaultValue: "Length (m)" })}</label>
                  <input
                    type="text"
                    value={lengthInput}
                    readOnly
                    className="superimposable-input superimposable-auto-calculated-input"
                    placeholder="0.00"
                  />
                </div>

                <div className="superimposable-load-dimension-field">
                  <label>{t("labels.width", { defaultValue: "Width (m)" })}</label>
                  <input
                    type="text"
                    value={widthInput}
                    readOnly
                    className="superimposable-input superimposable-auto-calculated-input"
                    placeholder="0.00"
                  />
                </div>

                <div className="superimposable-load-dimension-field">
                  <label>{t("labels.height", { defaultValue: "Height (m)" })}</label>
                  <input
                    type="text"
                    value={heightInput}
                    readOnly
                    className="superimposable-input superimposable-auto-calculated-input"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {!pointDimensionSummary.lengthMatches && (
                <p className="superimposable-dimension-warning-text">
                  {t("errors.pickupDeliveryLengthMismatch", {
                    defaultValue:
                      "⚠️ The sum of pickup and delivery lengths does not match yet.",
                  })}
                </p>
              )}

              <div className="superimposable-dimension-helper-text">
                {t("labels.loadDimensionsHelper", {
                  defaultValue:
                    "Length is calculated from the sum of points. Width and height use the highest registered value.",
                })}
              </div>
            </div>

            {/* 🆕 Trailer Type */}
            <div className="superimposable-field-group">
              <label className="superimposable-label">
                {t("trailerType.label", { defaultValue: "Trailer type" })}
              </label>

              <Select
                options={trailerTypeOptions}
                value={
                  trailerTypeOptions.find(
                    (o) => o.value === String(trailerType || "").trim().toUpperCase()
                  ) || null
                }
                onChange={handleTrailerTypeChange}
                isClearable
                placeholder={t("trailerType.placeholder", { defaultValue: "Select trailer type..." })}
                styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                classNamePrefix={`superimposable-select${(triedSubmit && !trailerType) || trailerTypeError ? "-error" : ""}`}
                className="superimposable-select-fullwidth"
                menuPortalTarget={document.body}
              />

              {(triedSubmit && !trailerType) || trailerTypeError ? (
                <p className="error-text">❌ {t("trailerType.errors.required", { defaultValue: "Trailer type is required." })}</p>
              ) : null}
            </div>

            {String(trailerType || "").trim().toUpperCase() === "OTHER" && (
              <div className="superimposable-field-group" style={{ marginTop: -6 }}>
                <label className="superimposable-label">
                  {t("trailerType.otherLabel", { defaultValue: "Other (specify)" })}
                </label>

                <input
                  type="text"
                  value={trailerTypeOther || ""}
                  onChange={(e) => {
                    const v = e.target.value ?? "";
                    setTrailerTypeOther(v);

                    // ✅ só ativa erro depois do submit
                    setTrailerTypeOtherError(triedSubmit ? !String(v).trim() : false);
                  }}
                  onFocus={handleBlockedInput}
                  readOnly={!selectedLoadBearer}
                  placeholder={t("trailerType.otherPlaceholder", { defaultValue: "Write trailer type..." })}
                  className={`superimposable-input ${trailerTypeOtherError ? "input-error" : ""}`}
                />

                {trailerTypeOtherError && (
                  <p className="error-text">
                    ❌ {t("trailerType.errors.otherRequired", { defaultValue: "Please specify the trailer type." })}
                  </p>
                )}
              </div>
            )}

            <h3 className="superimposable-column-title">{t("sections.generalInfo")}</h3>

            <div className="superimposable-field-group">
              <label className="superimposable-label">{t("labels.price")}*</label>
              <div className="superimposable-input-container">
                <input
                  type="text"
                  value={priceInput}
                  onChange={handlePriceChange}
                  onFocus={handleBlockedInput}
                  readOnly={!selectedLoadBearer}
                  className={`superimposable-input ${priceError ? "input-error" : ""}`}
                  placeholder={t("placeholders.priceExample")}
                  inputMode="decimal"
                />
              </div>
              {priceError && <p className="error-text">{priceError}</p>}
            </div>

            {/* 🆕 Price for Subcontracted (ONLY when external carrier) */}
            {showNotes4subcontracted && (
              <div className="superimposable-field-group">
                <label className="superimposable-label">{t("labels.price4subcontrated")}</label>

                <div className="superimposable-input-container">
                  <input
                    type="text"
                    value={price4subcontratedInput}
                    onChange={handlePrice4subcontratedChange}
                    onFocus={handleBlockedInput}
                    readOnly={!selectedLoadBearer}
                    className={`superimposable-input ${price4subcontratedError ? "input-error" : ""}`}
                    placeholder={t("placeholders.priceExample")}
                    inputMode="decimal"
                  />
                </div>

                {price4subcontratedError && <p className="error-text">{price4subcontratedError}</p>}
              </div>
            )}

            {/* Client Type */}
            <div className="superimposable-field-group">
              <label className="superimposable-label">{t("labels.clientType")}*</label>

              <Select
                value={
                  clientType
                    ? {
                        value: clientType,
                        label: clientType === "industry" ? t("options.industry") : t("options.carrier"),
                      }
                    : null
                }
                onChange={(selected) => {
                  if (!selectedLoadBearer) {
                    handleBlockedInput();
                    setForceCloseClientTypeSelect(true);
                    return;
                  }
                  const type = selected?.value || "";
                  setClientType(type);
                  setIndustryClientId("");
                  setCarrierClientId("");
                  setSelectedClientContactEmails([]);
                }}
                onMenuOpen={() => {
                  if (!selectedLoadBearer) {
                    handleBlockedInput();
                    setForceCloseClientTypeSelect(true);
                  } else {
                    setForceCloseClientTypeSelect(false);
                  }
                }}
                menuIsOpen={forceCloseClientTypeSelect ? false : undefined}
                options={[
                  { value: "industry", label: t("options.industry") },
                  { value: "carrier", label: t("options.carrier") },
                ]}
                classNamePrefix={"superimposable-select" + (triedSubmit && !clientType ? "-error" : "")}
                className="superimposable-select-fullwidth"
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                placeholder={t("placeholders.selectClientType")}
                isClearable
              />

              {triedSubmit && !clientType && <p className="error-text">❌ {t("errors.clientType.required")}</p>}
            </div>

            {/* Client */}
            {clientType && (
              <div className="superimposable-field-group">
                <label className="superimposable-label">
                  {clientType === "industry"
                    ? `${t("labels.industryClient")}*`
                    : `${t("labels.carrierClient")}*`}
                </label>

                <Select
                  value={
                    clientType === "industry"
                      ? industryClients
                          .map((c) => ({ value: c.id, label: c.companyName }))
                          .find((c) => c.value === industryClientId) || null
                      : carrierClients
                          .map((c) => ({ value: c.id, label: c.companyName }))
                          .find((c) => c.value === carrierClientId) || null
                  }
                  onChange={(selectedOption) => {
                    if (!selectedLoadBearer) {
                      handleBlockedInput();
                      setForceCloseClientSelect(true);
                      return;
                    }

                    setSelectedClientContactEmails([]);

                    if (clientType === "industry") setIndustryClientId(selectedOption ? selectedOption.value : "");
                    else setCarrierClientId(selectedOption ? selectedOption.value : "");
                  }}
                  onMenuOpen={() => {
                    if (!selectedLoadBearer) {
                      handleBlockedInput();
                      setForceCloseClientSelect(true);
                    } else {
                      setForceCloseClientSelect(false);
                    }
                  }}
                  menuIsOpen={forceCloseClientSelect ? false : undefined}
                  options={
                    clientType === "industry"
                      ? industryClients.map((c) => ({ value: c.id, label: c.companyName }))
                      : carrierClients.map((c) => ({ value: c.id, label: c.companyName }))
                  }
                  classNamePrefix={
                    "superimposable-select" + (triedSubmit && !industryClientId && !carrierClientId ? "-error" : "")
                  }
                  className="superimposable-select-fullwidth"
                  styles={customSelectStyles}
                  menuPortalTarget={document.body}
                  placeholder={t("placeholders.searchOrSelectClient")}
                  isClearable
                />

                {triedSubmit && !industryClientId && !carrierClientId && (
                  <p className="error-text">❌ {t("errors.client.required")}</p>
                )}

                {((clientType === "industry" && industryClientId) ||
                  (clientType === "carrier" && carrierClientId)) && (
                  <div className="superimposable-field-group">
                    <label className="superimposable-label">
                      {t("fields.clientContactEmails.label", {
                        defaultValue: "Client contact email(s)",
                      })}
                    </label>

                    <Select
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      options={selectedClientContactEmailOptions}
                      value={selectedClientContactEmails}
                      onChange={(selectedOptions) => {
                        const limited = Array.isArray(selectedOptions)
                          ? selectedOptions.slice(0, 3)
                          : [];
                        setSelectedClientContactEmails(limited);
                      }}
                      styles={multiSelectHorizontalStyles}
                      classNamePrefix="superimposable-select"
                      className="superimposable-select-fullwidth"
                      menuPortalTarget={document.body}
                      placeholder={t("fields.clientContactEmails.placeholder", {
                        defaultValue: "Select up to 3 contact email(s)...",
                      })}
                      noOptionsMessage={() =>
                        t("fields.clientContactEmails.noOptions", {
                          defaultValue: "No contact emails were found for this client.",
                        })
                      }
                    />

                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                      {t("fields.clientContactEmails.helper", {
                        defaultValue: "These emails come from the client's contact list.",
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Transport Carrier */}
            <div className="superimposable-field-group">
              <label className="superimposable-label">{t("labels.transportCarrier")}</label>

              <Select
                value={availableCarriers.find((c) => String(c.value) === String(carrierId)) || null}
                onChange={(selectedOption) => {
                  if (!selectedLoadBearer) {
                    handleBlockedInput();
                    return;
                  }
                  setCarrierId(selectedOption?.value ? String(selectedOption.value) : "");
                  setSelectedSubcontractedEmails([]);
                }}
                options={availableCarriers}
                classNamePrefix={`superimposable-select${triedSubmit && !carrierId ? "-error" : ""}`}
                className="superimposable-select-fullwidth"
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                placeholder={t("placeholders.selectTransportCarrier")}
                isClearable
                filterOption={(candidate, input) => {
                  const term = String(input || "").toLowerCase().trim();
                  if (!term) return true;
                  const data = candidate?.data || {};
                  return String(data.searchText || data.label || "").toLowerCase().includes(term);
                }}
              />

              {triedSubmit && !carrierId && (
                <p className="error-text">❌ {t("errors.transportCarrier.required")}</p>
              )}
            </div>

            {showNotes4subcontracted && (
              <div className="superimposable-field-group">
                <label className="superimposable-label">
                  {t("fields.subcontractedEmail.label", {
                    defaultValue: "Subcontracted Emails",
                  })}
                </label>

                <Select
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  options={selectedSubcontractedEmailOptions}
                  value={selectedSubcontractedEmails}
                  onChange={(selectedOptions) => {
                    const limited = Array.isArray(selectedOptions)
                      ? selectedOptions.slice(0, 3)
                      : [];
                    setSelectedSubcontractedEmails(limited);
                  }}
                  styles={multiSelectHorizontalStyles}
                  classNamePrefix="superimposable-select"
                  className="superimposable-select-fullwidth"
                  menuPortalTarget={document.body}
                  placeholder={t("fields.subcontractedEmail.placeholder", {
                    defaultValue: "Select up to 3 subcontracted email(s)...",
                  })}
                  noOptionsMessage={() =>
                    t("fields.subcontractedEmail.noOptions", {
                      defaultValue: "No contact emails found for this subcontracted carrier.",
                    })
                  }
                />

                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                  {t("fields.subcontractedEmail.helper", {
                    defaultValue: "These emails come from the subcontracted carrier's contact list.",
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Column 3: Unloading Points */}
          <div className="superimposable-column">
            <h3 className="superimposable-column-title">{t("sections.unloadingPoints")}</h3>

            {deliveryPlaces.map((place, index) => (
              <div key={index} className="multi-location-input">
                <div className="superimposable-delivery-label-row">
                  <label className="superimposable-label">{t("labels.deliveryPlace")}*</label>

                  {deliveryPlaces.length > 1 && (
                    <button
                      type="button"
                      className="superimposable-delete-pickup-btn"
                      onClick={() => removeDeliveryPlace(index)}
                      title={t("tooltips.removeDelivery")}
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="select-wrapper-left-icon">
                  <CreatableSelect
                    isClearable
                    isMulti={false}
                    options={placeOptions}
                    placeholder={t("placeholders.selectOrTypePlace")}
                    value={
                      place.placeId
                        ? placeOptions.find((p) => p.value === place.placeId) || null
                        : place.location
                        ? { label: place.location, value: place.location }
                        : null
                    }
                    onMenuOpen={() => {
                      if (!selectedLoadBearer) handleBlockedInput();
                    }}
                    onFocus={() => {
                      if (!selectedLoadBearer) handleBlockedInput();
                    }}
                    onChange={(selectedOption) => {
                      const updated = [...deliveryPlaces];

                      if (selectedOption?.__isNew__) {
                        updated[index] = {
                          ...updated[index],
                          placeId: null,
                          location: selectedOption.label,
                          address: "",
                          postalCode: "",
                          city: "",
                          country: "",
                          gMapsLink: "",
                          coordinates: "",
                          contactPhone: "",
                          loadingAccess: "SIDE",
                          loadingAccessError: false,

                          handlingEquipment: "FORKLIFT",
                          handlingEquipmentError: false,
                          handlingEquipmentOther: "",
                          handlingEquipmentOtherError: false,
                        };
                      } else if (selectedOption?.fullData) {
                        const p = selectedOption.fullData;
                        updated[index] = {
                          ...updated[index],
                          placeId: p.id,
                          location: p.placeName,
                          address: p.address,
                          postalCode: p.postalCode,
                          city: p.city,
                          country: p.country,
                          gMapsLink: p.gMapsLink,
                          coordinates: p.coordinates,
                          contactPhone: p.contactPhone,
                          // 🆕 autofill
                          loadingAccess: p.loadingAccess || "SIDE",
                          loadingAccessError: false,

                          // 🆕 Handling equipment
                          handlingEquipment: p.handlingEquipment || "FORKLIFT",
                          handlingEquipmentError: false,
                          handlingEquipmentOther: p.handlingEquipmentOther || "",
                          handlingEquipmentOtherError: false,

                          // 🆕 reference (optional)
                          reference: "",
                        };
                      } else {
                        updated[index] = {
                          ...updated[index],
                          placeId: null,
                          location: selectedOption?.label || "",
                          address: "",
                          postalCode: "",
                          city: "",
                          country: "",
                          gMapsLink: "",
                          coordinates: "",
                          contactPhone: "",
                          loadingAccess: "SIDE",
                          loadingAccessError: false,

                          handlingEquipment: "FORKLIFT",
                          handlingEquipmentError: false,
                          handlingEquipmentOther: "",
                          handlingEquipmentOtherError: false,
                          
                          // 🆕 reference (optional)
                          reference: "",
                        };
                      }

                      updated[index].locationError = !updated[index].location?.trim();
                      setDeliveryPlaces(updated);
                    }}
                    styles={customSelectStyles}
                    classNamePrefix={`superimposable-select${place.locationError ? "-error" : ""}`}
                  />
                </div>

                {place.locationError && <p className="error-text">❌ {t("errors.location.required")}</p>}

                <div className="input-row tighter-row">
                  <div className="custom-input-wrapper">
                    <input
                      type="date"
                      value={place.date || ""}
                      onFocus={handleBlockedInput}
                      onChange={(e) => handleDeliveryPlaceChange(e, index, "date")}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value.length === 10) validateDateOnBlur(value, index, "delivery");
                      }}
                      className={`superimposable-input ${place.dateError ? "input-error" : ""}`}
                      ref={(el) => {
                        if (el) deliveryDateRefs.current[index] = el;
                      }}
                    />

                    <FaCalendarAlt
                      className="input-icon clickable"
                      onClick={(e) => {
                        e.preventDefault();
                        const input = deliveryDateRefs.current[index];
                        if (input && typeof input.showPicker === "function") {
                          input.focus();
                          setTimeout(() => input.showPicker(), 0);
                        }
                      }}
                    />
                  </div>

                  <div className="custom-input-wrapper">
                    <input
                      type="text"
                      placeholder={t("placeholders.hhmm")}
                      value={place.hour || ""}
                      readOnly={!selectedLoadBearer}
                      onFocus={handleBlockedInput}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, "");
                        let formatted = raw;
                        if (raw.length >= 3) formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;

                        const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                        const isValid = regex.test(formatted);

                        const updated = [...deliveryPlaces];
                        updated[index].hour = formatted;
                        updated[index].hourError = formatted !== "" && !isValid;
                        setDeliveryPlaces(updated);
                      }}
                      onBlur={() => {
                        const updated = [...deliveryPlaces];
                        const hour = updated[index].hour;
                        const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                        updated[index].hourError = hour && !regex.test(hour);
                        setDeliveryPlaces(updated);
                        handleHourBlur();
                      }}
                      className={`superimposable-input ${place.hourError ? "input-error" : ""}`}
                      ref={(el) => {
                        if (el) deliveryHourRefs.current[index] = el;
                      }}
                    />

                    <FaClock
                      className="input-icon clickable"
                      onClick={(e) => {
                        e.preventDefault();
                        const input = deliveryHourRefs.current[index];
                        if (input && typeof input.showPicker === "function") {
                          input.focus();
                          setTimeout(() => input.showPicker(), 0);
                        }
                      }}
                    />
                  </div>
                </div>

                {place.dateError && (
                  <p className="error-text">
                    ❌{" "}
                    {place.date && place.hour
                      ? t("errors.deliveryBeforePickupInline")
                      : t("errors.date.required")}
                  </p>
                )}

                {place.hourError && (
                  <p className="error-text">
                    ❌{" "}
                    {place.hour
                      ? t("errors.hour.invalidFormat")
                      : t("errors.hour.required")}
                  </p>
                )}

                <div className="superimposable-point-dimensions-block">
                  <label className="superimposable-label superimposable-point-dimensions-title">
                    {t("labels.pointDimensions", {
                      defaultValue: "Load dimensions at this point (in meters)",
                    })}
                  </label>

                  <div className="superimposable-point-dimensions-row">
                    <div className="superimposable-point-dimension-field">
                      <label>{t("labels.pointLengthShort", { defaultValue: "Length" })}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={place.length || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "length")}
                        className={`superimposable-input ${place.lengthError ? "input-error" : ""}`}
                        placeholder="0.00m"
                      />
                      {place.lengthError && <p className="error-text">{place.lengthError}</p>}
                    </div>

                    <div className="superimposable-point-dimension-field">
                      <label>{t("labels.pointWidthShort", { defaultValue: "Width" })}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={place.width || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "width")}
                        className={`superimposable-input ${place.widthError ? "input-error" : ""}`}
                        placeholder="0.00m"
                      />
                      {place.widthError && <p className="error-text">{place.widthError}</p>}
                    </div>

                    <div className="superimposable-point-dimension-field">
                      <label>{t("labels.pointHeightShort", { defaultValue: "Height" })}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={place.height || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "height")}
                        className={`superimposable-input ${place.heightError ? "input-error" : ""}`}
                        placeholder="0.00m"
                      />
                      {place.heightError && <p className="error-text">{place.heightError}</p>}
                    </div>
                  </div>
                </div>

                {/* 🆕 Loading Access */}
                <label className="superimposable-label">
                  {t("loadingAccess.label", { defaultValue: "Loading access" })}
                </label>

                <div className="superimposable-select-wrapper" style={{ marginTop: -6, marginBottom: 5 }}>
                  <Select
                    options={loadingAccessOptions}
                    value={
                      loadingAccessOptions.find(
                        (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                      ) || null
                    }
                    onChange={(opt) => handleLoadingAccessChange(index, opt, true)}
                    isClearable
                    placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                    styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    classNamePrefix={`superimposable-select${(triedSubmit && place.loadingAccessError) ? "-error" : ""}`}
                    menuPortalTarget={document.body}
                  />
                </div>

                {triedSubmit && place.loadingAccessError && (
                  <p className="error-text">❌ {t("loadingAccess.errors.required", { defaultValue: "Loading access is required." })}</p>
                )}

                {/* 🆕 Handling Equipment */}
                <label className="superimposable-label">
                  {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                </label>

                <div className="superimposable-select-wrapper" style={{ marginTop: -6, marginBottom: 5 }}>
                  <Select
                    options={handlingEquipmentOptions}
                    value={
                      handlingEquipmentOptions.find(
                        (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                      ) || null
                    }
                    onChange={(opt) => handleHandlingEquipmentChange(index, opt, true)}
                    isClearable
                    placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select handling equipment..." })}
                    styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    classNamePrefix={`superimposable-select${place.handlingEquipmentError ? "-error" : ""}`}
                    menuPortalTarget={document.body}
                  />
                </div>

                {place.handlingEquipmentError && (
                  <p className="error-text">❌ {t("handlingEquipment.errors.required", { defaultValue: "Handling equipment is required." })}</p>
                )}

                {String(place.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                  <div className="superimposable-field-group" style={{ marginTop: -6, marginBottom: 10 }}>
                    <label className="superimposable-label">
                      {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                    </label>

                    <input
                      type="text"
                      placeholder={t("fields.placeholders.handlingEquipmentOther", { defaultValue: "Write the equipment..." })}
                      value={place.handlingEquipmentOther || ""}
                      onChange={(e) => handleDeliveryPlaceChange(e, index, "handlingEquipmentOther")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.handlingEquipmentOtherError ? "input-error" : ""}`}
                    />

                    {place.handlingEquipmentOtherError && (
                      <p className="error-text">
                        ❌ {t("fields.errors.handlingEquipmentOtherRequired", { defaultValue: "Other handling equipment is required." })}
                      </p>
                    )}
                  </div>
                )}

                {/* 🆕 Reference (optional) */}
                <div className="superimposable-field-group" style={{ marginTop: -6, marginBottom: 10 }}>
                  <label className="superimposable-label">{t("labels.reference", { defaultValue: "Reference" })}</label>

                  <input
                    type="text"
                    placeholder={t("placeholders.reference", { defaultValue: "e.g.: PO 12345, customer ref..." })}
                    value={place.reference || ""}
                    onChange={(e) => handleDeliveryPlaceChange(e, index, "reference")}
                    onFocus={handleBlockedInput}
                    readOnly={!selectedLoadBearer}
                    className="superimposable-input"
                  />
                </div>

                {/* Extra fields */}
                {place.showMoreFields ? (
                  <>
                    <input
                      type="text"
                      placeholder={t("placeholders.address")}
                      value={place.address || ""}
                      onChange={(e) => handleDeliveryPlaceChange(e, index, "address")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.addressError ? "input-error" : ""}`}
                    />
                    {place.addressError && <p className="error-text">❌ {t("errors.address.required")}</p>}

                    <div className="superimposable-row">
                      <input
                        type="text"
                        placeholder={t("placeholders.postalCode")}
                        value={place.postalCode || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "postalCode")}
                        onFocus={handleBlockedInput}
                        readOnly={!selectedLoadBearer}
                        className={`superimposable-input ${place.postalCodeError ? "input-error" : ""}`}
                      />
                      {place.postalCodeError && <p className="error-text">❌ {t("errors.postalCode.required")}</p>}

                      <input
                        type="text"
                        placeholder={t("placeholders.city")}
                        value={place.city || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "city")}
                        onFocus={handleBlockedInput}
                        readOnly={!selectedLoadBearer}
                        className={`superimposable-input ${place.cityError ? "input-error" : ""}`}
                      />
                      {place.cityError && <p className="error-text">❌ {t("errors.city.required")}</p>}

                      <input
                        type="text"
                        placeholder={t("placeholders.country")}
                        value={place.country || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "country")}
                        onFocus={handleBlockedInput}
                        readOnly={!selectedLoadBearer}
                        className={`superimposable-input ${place.countryError ? "input-error" : ""}`}
                      />
                      {place.countryError && <p className="error-text">❌ {t("errors.country.required")}</p>}
                    </div>

                    <input
                      type="text"
                      placeholder={t("placeholders.contactPhone")}
                      value={place.contactPhone || ""}
                      onChange={(e) => handleDeliveryPlaceChange(e, index, "contactPhone")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.contactPhoneError ? "input-error" : ""}`}
                    />
                    {place.contactPhoneError && <p className="error-text">❌ {t("errors.contactPhone.required")}</p>}

                    <input
                      type="text"
                      placeholder={t("placeholders.gMapsLink")}
                      value={place.gMapsLink || ""}
                      onChange={(e) => handleDeliveryPlaceChange(e, index, "gMapsLink")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.gMapsLinkError ? "input-error" : ""}`}
                    />
                    {place.gMapsLinkError && <p className="error-text">❌ {t("errors.gMapsLink.required")}</p>}

                    <input
                      type="text"
                      placeholder={t("placeholders.coordinates")}
                      value={place.coordinates || ""}
                      onChange={(e) => handleDeliveryPlaceChange(e, index, "coordinates")}
                      onFocus={handleBlockedInput}
                      readOnly={!selectedLoadBearer}
                      className={`superimposable-input ${place.coordinatesError ? "input-error" : ""}`}
                    />
                    {place.coordinatesError && <p className="error-text">❌ {t("errors.coordinates.required")}</p>}

                    <div className="toggle-fields-wrapper">
                      <button
                        type="button"
                        className="superimposable-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...deliveryPlaces];
                          updated[index].showMoreFields = false;
                          setDeliveryPlaces(updated);
                        }}
                      >
                        {t("buttons.hideFields")}
                        <FiChevronUp className="superimposable-toggle-arrow" />
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="toggle-fields-wrapper">
                    <button
                      type="button"
                      className="superimposable-toggle-fields-btn"
                      onClick={() => {
                        const updated = [...deliveryPlaces];
                        updated[index].showMoreFields = true;
                        setDeliveryPlaces(updated);
                      }}
                    >
                      {t("buttons.moreFields")}
                      <FiChevronDown className="superimposable-toggle-arrow" />
                    </button>
                  </div>
                )}
              </div>
            ))}

            {deliveryPlaces.length < MAX_POINTS && (
              <button className="superimposable-add-btn" onClick={addDeliveryPlace}>
                {t("buttons.addAnotherDelivery")}
              </button>
            )}

            {deliveryPlaces.length >= MAX_POINTS && (
              <p className="superimposable-error-text-max-points">{t("warnings.maxDeliveriesReached")}</p>
            )}

            {deliveryPlaceError && <p className="error-text">{deliveryPlaceError}</p>}
          </div>
        </div>

        {/* Global errors */}
        {error && <p className="error-text">{error}</p>}
        {globalHourFormatError && <p className="error-text">{t("errors.hour.globalInvalid")}</p>}

        {/* 🆕 Notes for Subcontracted (ONLY when external carrier) */}
        {showNotes4subcontracted && (
          <div className="superimposable-field-group">
            <label className="superimposable-label">{t("labels.notesForSubcontracted")}</label>

            <textarea
              rows={3}
              placeholder={t("placeholders.notesForSubcontracted")}
              value={notes4subcontracted}
              onChange={(e) => {
                const v = e.target.value ?? "";
                if (v.length <= 2000) {
                  setNotes4subcontracted(v);
                  if (notes4subcontractedError) setNotes4subcontractedError("");
                } else {
                  setNotes4subcontractedError(t("errors.notes.maxChars"));
                }
              }}
              onFocus={handleBlockedInput}
              readOnly={!selectedLoadBearer}
              className={`superimposable-input ${notes4subcontractedError ? "input-error" : ""}`}
            />

            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.75 }}>
              <span>{t("helpers.notesOnlySubcontractedCarrier")}</span>
              <span>{t("helpers.charCount", { count: (notes4subcontracted || "").length })}</span>
            </div>

            {notes4subcontractedError && <p className="error-text">{notes4subcontractedError}</p>}
          </div>
        )}

        {/* Notes for Driver */}
        <div className="superimposable-field-group">
          <label className="superimposable-label">{t("labels.notesForDriver")}</label>
          <textarea
            rows={3}
            placeholder={t("placeholders.notesForDriver")}
            value={notes4driver}
            onChange={(e) => {
              const v = e.target.value;
              if (v.length <= 2000) {
                setNotes4driver(v);
                if (notes4driverError) setNotes4driverError("");
              } else {
                setNotes4driverError(t("errors.notes.maxChars"));
              }
            }}
            onFocus={handleBlockedInput}
            readOnly={!selectedLoadBearer}
            className={`superimposable-input ${notes4driverError ? "input-error" : ""}`}
          />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.75 }}>
            <span>{t("helpers.notesOnlyDriverApp")}</span>
            <span>{t("helpers.charCount", { count: notes4driver.length })}</span>
          </div>
          {notes4driverError && <p className="error-text">{notes4driverError}</p>}
        </div>

        {/* Buttons */}
        <div className="superimposable-create-button-group">
          <button className="superimposable-create-cancel-button" onClick={() => navigate("/loads")}>
            {t("buttons.cancel")}
          </button>

          <button
            className="superimposable-create-main-button"
            disabled={
              !selectedLoadBearer ||
              loading ||
              !!lengthError ||
              !!heightError ||
              !!widthError ||
              !!priceError ||
              !!price4subcontratedError ||
              !!notes4driverError ||
              !!merchandiseError ||
              !!weightError ||
              !!trailerTypeError ||
              !!trailerTypeOtherError
              // 🆕 handling equipment errors (por ponto) não são globais, mas isto evita submit quando estiverem setados
            }
            onClick={handleCreateSuperImposableLoad}
          >
            {loading ? t("buttons.creating") : t("buttons.create")}
          </button>
        </div>

        {message && <p className="success-text">{message}</p>}
      </div>
    </div>
  );
};

export default SuperImposableLoad;
